import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { List } from 'react-window';
import { useAuth } from './AuthContext';
import { apiGetProjectSourceTree, apiDeleteSourceFiles, apiUploadFilesToFolder, isZipUpload, apiGetCommand, apiAddToCommandQueue, apiDeleteCommandQueue, apiDownloadGeneratedCode } from './api';
import { useGetProjectCommandsQuery, useGetCommandQueueQuery, EMPTY_ARRAY } from './store/futurizerApi';
import { isNodeFolder, sortNodes, FileTreeView, pbTypeFor, formatFileSize } from './FileTree';
import PageLoader from './PageLoader';
import { apiTimestampToDate, fmtDate, fmtRelative, formatDate } from './dateUtils';

// Builds the JSON payload the command-processing consumer expects for SourceFileInfo:
// an array of { fileName, extension, sourceFilePath, type }, one per selected file. When a
// command's inputSchema assigns files to specific controls (see parseInputSchema /
// CommandInputFields below), typeMap carries relativePath -> control.type so each file
// is tagged the same way the backend already tags them (e.g. "API Defination",
// "Functional Analysis"); anything left unassigned — including every file for commands
// with no inputSchema at all, e.g. when a control's own file list doesn't come back —
// always falls back to "source code" rather than omitting the type.
function buildSourceFileList(resourcePath, relativePaths, typeMap = {}) {
  if (!relativePaths.length) return [];
  return relativePaths.map(relativePath => {
    const segments = relativePath.split('/');
    const fileName = segments[segments.length - 1];
    const dotIndex = fileName.lastIndexOf('.');
    const extension = dotIndex >= 0 ? fileName.slice(dotIndex) : '';
    const sourceFilePath = `${resourcePath}/${segments.join('/')}`;
    const type = typeMap[relativePath] ?? 'source code';
    return { fileName, extension, sourceFilePath, type };
  });
}

// A command's inputSchema (see GET /Command/{commandId}) is a JSON string like:
//   { "popupTitle": "...", "controls": [{ id, text, type, multiSelect, required }] }
// Returns the parsed object only when it actually has a usable controls array —
// commands without a structured input form (e.g. "Generate Dependency Graph") have
// inputSchema: null and skip the picker entirely.
function parseInputSchema(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.controls) && parsed.controls.length ? parsed : null;
  } catch {
    return null;
  }
}

function parseSourceFileInfo(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// CommandQueue.resultFileInfo is the GeneratedResult/Q{id} folder tree (the
// contents of the result zip) as a JSON string with PascalCase keys — Name,
// NodeType, Children — mapped here to the camelCase shape FileTreeView reads.
function parseResultFileInfo(raw) {
  if (!raw) return null;
  function toNode(n) {
    return {
      name:     n.Name ?? n.name ?? '?',
      nodeType: n.NodeType ?? n.nodeType,
      children: (n.Children ?? n.children ?? []).map(toNode),
    };
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? toNode(parsed) : null;
  } catch {
    return null;
  }
}

// "Contents" row of an expanded task/result — what's inside its result zip.
function ResultContentsItem({ task }) {
  if (task.status !== 'PROCESSED') return null;
  return (
    <div style={S.taskDetailItem}>
      <span style={S.taskDetailLabel}>Contents</span>
      {task.resultTree ? (
        <div style={S.taskFileTreeBox}>
          <FileTreeView node={task.resultTree} query="" />
        </div>
      ) : (
        <span style={S.taskDetailVal}>Not available</span>
      )}
    </div>
  );
}

// Backend sourceFileInfo entries don't reliably carry relativePath (missing on some
// rows), so the folder tree is always derived from sourceFilePath instead — taking
// whatever comes after the "Source" folder in it, and normalizing the mix of '/'
// and '\' separators the backend produces.
function relativePathFromSourceFilePath(sourceFilePath) {
  if (!sourceFilePath) return null;
  const normalized = sourceFilePath.replace(/\\/g, '/');
  const idx = normalized.search(/\/Source\//i);
  if (idx === -1) return null;
  return normalized.slice(idx + '/Source/'.length);
}

// Reassembles the flat { fileName, sourceFilePath } list stored on a task back into
// the folder/file node shape FileTreeView expects, so tasks render the same tree as Source.
function buildFileTreeFromList(files) {
  const root = { type: 'folder', name: 'Files', children: [] };
  files.forEach(f => {
    const relPath = relativePathFromSourceFilePath(f.sourceFilePath) ?? f.fileName ?? '';
    const segments = relPath.split('/').filter(Boolean);
    if (!segments.length) return;
    let node = root;
    segments.forEach((seg, i) => {
      const isLast = i === segments.length - 1;
      if (isLast) {
        node.children.push({ type: 'file', name: seg });
        return;
      }
      let child = node.children.find(c => c.type === 'folder' && c.name === seg);
      if (!child) {
        child = { type: 'folder', name: seg, children: [] };
        node.children.push(child);
      }
      node = child;
    });
  });
  return root;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const FILE_ICONS = {
  jsx: '⚛️', js: '🟨', json: '📋', md: '📝',
  xml: '📄', html: '🌐', css: '🎨', ts: '🟦', tsx: '⚛️',
  zip: '🗜️', pdf: '📕', txt: '📃',
};
function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  return pbTypeFor(name)?.icon ?? FILE_ICONS[ext] ?? '📄';
}
function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// The queue endpoint returns createdByName ("LastName, FirstName", null when the
// user no longer exists); fall back to the signed-in user's name, then the id.
function taskUserLabel(createdBy, createdByName, user) {
  if (createdByName) return createdByName;
  if (createdBy != null && createdBy === user?.id && user?.name) return user.name;
  return createdBy != null ? `User #${createdBy}` : '—';
}

// One option per distinct task creator, the signed-in user first.
function buildUserOptions(tasks, user) {
  const byId = new Map();
  tasks.forEach(t => {
    if (t.createdBy != null && !byId.has(t.createdBy)) byId.set(t.createdBy, t.queuedBy);
  });
  return [...byId.entries()]
    .map(([id, label]) => ({ id, label: id === user?.id ? `${label} (me)` : label, isMe: id === user?.id }))
    .sort((a, b) => (b.isMe - a.isMe) || a.label.localeCompare(b.label));
}

// "👤 mayank.kul · 26 Aug 2026, 09:18 AM" — who queued the task and when.
function UserTag({ name, at }) {
  return (
    <span style={S.userTag} title={`Queued by ${name} · ${fmtRelative(at)}`}>
      👤 <span style={S.userTagName}>{name}</span>
      <span style={S.userTagSep}>·</span>
      {formatDate(at)}
    </span>
  );
}

function matchesUser(item, userFilter) {
  return userFilter === 'all' || String(item.createdBy) === userFilter;
}

function UserFilterSelect({ value, onChange, options }) {
  return (
    <>
      <span style={S.taskFilterLabel}>User</span>
      <select style={S.taskSelect} value={value} onChange={e => onChange(e.target.value)}>
        <option value="all">All users</option>
        {options.map(o => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
      </select>
    </>
  );
}
// ── Main component ────────────────────────────────────────────────────────────

export default function AIProcessPage() {
  const { project, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('tab') || 'source';
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [tasks, setTasks]     = useState([]);
  const [commands, setCommands] = useState([]);
  const results = useMemo(() => tasks
    .filter(t => t.status === 'PROCESSED')
    .map(t => ({
      id: t.id,
      title: t.commandName ?? 'Result',
      type: 'zip',
      taskId: t.id,
      createdBy: t.createdBy,
      queuedBy: t.queuedBy,
      createdAt: t.createdAt,
      updatedAt: t.updatedAt ?? t.completedAt ?? t.createdAt,
      size: null,
      content: null,
    })), [tasks]);
  const [jumpToTask, setJumpToTask]     = useState(null);
  const [jumpToResult, setJumpToResult] = useState(null);
  const [confirmDeleteTask, setConfirmDeleteTask] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [refreshingTasks, setRefreshingTasks] = useState(false);
  // Shared by the Tasks and Results tabs so the chosen user survives a tab switch.
  const [userFilter, setUserFilter] = useState('all');
  const userOptions = useMemo(() => buildUserOptions(tasks, user), [tasks, user]);

  // Commands + the command queue, via RTK Query (src/store/futurizerApi.js):
  // cached in Redux and served instantly on remount. The queue still fetches
  // once whenever a project loads — ResultsPanel's detail lookup and
  // CommandsPanel's active-queue table/badge both read this same `tasks`
  // data even when the Tasks tab isn't open, so we can't skip the query
  // outright on other tabs. But the repeating 30s poll (which only matters
  // for picking up in-progress status changes live) only runs while the
  // Tasks tab is actually the one showing it — no point hitting the API
  // every 30s in the background while the user is on Source/Results/Commands.
  const {
    data: commandRowsRaw,
    isLoading: commandsLoading,
    refetch: refetchCommands,
  } = useGetProjectCommandsQuery(project?.id, { skip: !project?.id });
  const {
    data: queueRowsRaw,
    isLoading: queueLoading,
    refetch: refetchQueue,
  } = useGetCommandQueueQuery(project?.id, {
    skip: !project?.id,
    pollingInterval: activeSection === 'tasks' ? 30000 : 0,
  });
  // Referentially-stable fallback — see EMPTY_ARRAY's own comment for why a
  // `data: x = []` default here would infinite-loop instead of just missing data.
  const commandRows = commandRowsRaw ?? EMPTY_ARRAY;
  const queueRows = queueRowsRaw ?? EMPTY_ARRAY;

  // Turning pollingInterval on above only arms the *next* 30s tick — RTK Query
  // won't refetch immediately just because polling started, so without this
  // a tab switch would show whatever was last cached (possibly stale by up to
  // 30s) until the first tick lands. Force one fetch right away on entering
  // the Tasks tab, then let the poll above keep it current every 30s after.
  useEffect(() => {
    if (activeSection === 'tasks') refetchQueue();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadingTasks = commandsLoading || queueLoading;

  // The queue endpoint only returns fkCommandId, not the command's name — so the
  // command list (which does carry commandName) is used to resolve it.
  const mappedCommands = useMemo(() => commandRows.map((r, i) => ({
    id:          r.fkCommandId,
    order:       i + 1,
    name:        r.commandName   ?? `Command #${r.fkCommandId}`,
    displayName: r.displayName   ?? r.commandName ?? `Command #${r.fkCommandId}`,
    description: r.commandType   ?? '',
    params:      [],
  })), [commandRows]);

  const queueTasks = useMemo(() => queueRows.map(q => {
    const files = parseSourceFileInfo(q.sourceFileInfo);
    const cmd = mappedCommands.find(c => c.id === q.fkCommandId);
    return {
      id:          q.commandQueueId,
      commandId:   q.fkCommandId,
      commandName: cmd?.name ?? q.commandName ?? `Command #${q.fkCommandId}`,
      status:      q.queueStatus ?? 'QUEUED',
      files,
      fileCount:   files ? files.length : null,
      queuedBy:    taskUserLabel(q.createdBy, q.createdByName, user),
      createdBy:   q.createdBy,
      createdAt:   q.createdAt,
      updatedAt:   q.updatedAt ?? q.createdAt,
      completedAt: q.completedAt,
      processingNotes: q.processingNotes ?? null,
      errorDetails: q.queueStatus === 'FAILED' ? (q.processingNotes ?? null) : null,
      resultTree:  parseResultFileInfo(q.resultFileInfo),
      output:      null,
      outputId:    null,
    };
  }), [queueRows, mappedCommands, user]);

  // Mirrored into local state (rather than used directly) so the optimistic
  // local pushes/removals in handleTaskCreated/handleTaskDeleted below keep
  // working unchanged — each fresh load or poll tick just re-seeds from the
  // query's latest cached data.
  useEffect(() => { setCommands(mappedCommands); }, [mappedCommands]);
  useEffect(() => { setTasks(queueTasks); }, [queueTasks]);

  function handleRefreshTasks() {
    setRefreshingTasks(true);
    Promise.all([refetchCommands(), refetchQueue()]).finally(() => setRefreshingTasks(false));
  }

  function setSection(tab) { setSearchParams({ tab }); }

  function handleTaskCreated(task) {
    setTasks(prev => [task, ...prev]);
  }

  function handleTaskDeleted(taskId) {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }

  async function handleConfirmDelete() {
    if (!confirmDeleteTask) return;
    setDeleting(true);
    try {
      await apiDeleteCommandQueue(confirmDeleteTask.id);
      handleTaskDeleted(confirmDeleteTask.id);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
      setConfirmDeleteTask(null);
    }
  }

  function goToResult(id) { setJumpToResult(id); setSection('results'); }

  return (
    <div style={S.page}>
      {/* ── Content ── */}
      <div style={S.content}>
        {activeSection === 'source' && (
          <SourcePanel
            project={project}
            user={user}
            commands={commands}
            selectedFiles={selectedFiles}
            onSelectionChange={setSelectedFiles}
            tasks={tasks}
            onTaskCreated={handleTaskCreated}
            onExecuteSuccess={() => setSection('tasks')}
          />
        )}
        {activeSection === 'tasks' && (
          <TasksPanel
            tasks={tasks}
            user={user}
            jumpToId={jumpToTask}
            onJumpClear={() => setJumpToTask(null)}
            onViewResult={goToResult}
            onRequestDelete={setConfirmDeleteTask}
            onRefresh={handleRefreshTasks}
            refreshing={refreshingTasks}
            loading={loadingTasks}
            userFilter={userFilter}
            onUserFilterChange={setUserFilter}
            userOptions={userOptions}
          />
        )}
        {activeSection === 'results' && (
          <ResultsPanel
            results={results}
            tasks={tasks}
            jumpToId={jumpToResult}
            onJumpClear={() => setJumpToResult(null)}
            loading={loadingTasks}
            userFilter={userFilter}
            onUserFilterChange={setUserFilter}
            userOptions={userOptions}
          />
        )}
        {activeSection === 'commands' && (
          <CommandsPanel
            project={project}
            commands={commands}
            tasks={tasks}
            onTaskCreated={handleTaskCreated}
          />
        )}
      </div>

      {confirmDeleteTask && (
        <div style={S.modalOverlay} onClick={() => !deleting && setConfirmDeleteTask(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>Delete queued task?</div>
            <div style={S.modalBody}>
              This will remove <strong>{confirmDeleteTask.commandName ?? confirmDeleteTask.title ?? 'this task'}</strong> from the queue. This action cannot be undone.
            </div>
            <div style={S.modalActions}>
              <button style={S.modalCancelBtn} disabled={deleting} onClick={() => setConfirmDeleteTask(null)}>
                Cancel
              </button>
              <button style={S.modalDeleteBtn} disabled={deleting} onClick={handleConfirmDelete}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Source panel ──────────────────────────────────────────────────────────────

// An empty folder has nothing underneath it to select, so it's treated as a
// single selectable unit — its own path — rather than always collecting/
// counting to zero. Without this, an empty folder's checkbox could never
// become checked (0 selected out of a total of 0 never satisfies "select
// all"), so clicking it looked like the checkbox was simply broken.
function collectPaths(node, path, out) {
  const name = node.name ?? node.fileName ?? '?';
  const full = path ? `${path}/${name}` : name;
  if (isNodeFolder(node)) {
    const children = sortNodes(node.children ?? node.files ?? node.items ?? []);
    if (children.length === 0) {
      out.push(full);
      return;
    }
    children.forEach(c => collectPaths(c, full, out));
  } else {
    out.push(full);
  }
}

// Turns a flat set of selected leaf paths into the minimal list of delete
// targets: whenever every item under a folder is selected, that folder's own
// path replaces its individual entries — so the backend's recursive folder
// delete actually removes the folder itself instead of just emptying it out
// (the same reasoning as requestDeleteNode, applied to "Delete Selected").
function collapseToDeletablePaths(nodes, path, selectedSet, out) {
  sortNodes(nodes).forEach(node => {
    const name = node.name ?? node.fileName ?? '?';
    const full = path ? `${path}/${name}` : name;
    if (isNodeFolder(node)) {
      const children = node.children ?? node.files ?? node.items ?? [];
      if (children.length === 0) {
        if (selectedSet.has(full)) out.push(full);
        return;
      }
      const leaves = [];
      collectPaths(node, path, leaves);
      if (leaves.length > 0 && leaves.every(p => selectedSet.has(p))) {
        out.push(full);
      } else {
        collapseToDeletablePaths(children, full, selectedSet, out);
      }
    } else if (selectedSet.has(full)) {
      out.push(full);
    }
  });
}

function countSelInNode(node, path, sel) {
  const name = node.name ?? node.fileName ?? '?';
  const full = path ? `${path}/${name}` : name;
  if (isNodeFolder(node)) {
    const children = sortNodes(node.children ?? node.files ?? node.items ?? []);
    if (children.length === 0) return sel.includes(full) ? 1 : 0;
    return children.reduce((s, c) => s + countSelInNode(c, full, sel), 0);
  }
  return sel.includes(full) ? 1 : 0;
}

function countAllInNode(node) {
  if (isNodeFolder(node)) {
    const children = sortNodes(node.children ?? node.files ?? node.items ?? []);
    if (children.length === 0) return 1;
    return children.reduce((s, c) => s + countAllInNode(c), 0);
  }
  return 1;
}

const STATUS_CFG_SRC = {
  QUEUED:     { label: 'QUEUED',     color: '#8b949e', bg: 'rgba(139,148,158,0.12)' },
  PROCESSING: { label: 'PROCESSING', color: '#58a6ff', bg: 'rgba(88,166,255,0.12)'  },
  PROCESSED:  { label: 'PROCESSED',  color: '#3fb950', bg: 'rgba(63,185,80,0.12)'   },
  FAILED:     { label: 'FAILED',     color: '#f85149', bg: 'rgba(248,81,73,0.12)'   },
};

function SourcePanel({ project, user, commands, selectedFiles, onSelectionChange, tasks, onTaskCreated, onExecuteSuccess }) {
  const [step, setStep]               = useState(1);
  const [tree, setTree]               = useState(null);
  const [resourcePath, setResourcePath] = useState('');
  const [loading, setLoading]         = useState(true);
  const [selectedCmd, setSelectedCmd] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [hoveredCmd, setHoveredCmd]   = useState(null);
  const [lastQueued, setLastQueued]   = useState(null);
  const [cmdSearch, setCmdSearch]     = useState('');
  const [fileSearch, setFileSearch]   = useState('');
  // Collapsed by default, and which folders are open is tracked explicitly (rather
  // than mounting the whole subtree) — see VirtualSourceTree above.
  const [treeExpanded, setTreeExpanded] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(() => new Set());
  // Folders left collapsed by the last bulk expand because they're too large
  // (see AUTO_EXPAND_MAX_ITEMS) — surfaced next to the Expand All button so
  // hitting the cap doesn't just look like the button silently doing nothing.
  const [skippedFolders, setSkippedFolders] = useState([]);
  const [cmdDetail, setCmdDetail] = useState(null);       // last GET /Command/{commandId} result
  const [advancingToStep3, setAdvancingToStep3] = useState(false);
  // Row-level and bulk "delete from source" (FPB-70) — null | { label, count, paths }.
  // `paths` is always a flat list of leaf file paths: a folder delete is expanded
  // to every file under it up front, so the API call and the selectedFiles filter
  // below both work off the same simple list.
  const [confirmDeleteTarget, setConfirmDeleteTarget] = useState(null);
  const [deletingPaths, setDeletingPaths] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Right-click "Upload files/folder here" on a folder — null | { folderPath, label, mode }
  // where mode is 'files' | 'folder'. uploadFiles is a list of { file, path }, `path`
  // being the file's name plus any sub-folder it came from in a folder upload.
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadFilesError, setUploadFilesError] = useState('');
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const uploadFilesRef = useRef(null);
  const [inputSelections, setInputSelections] = useState({}); // { [controlId]: string | string[] }
  const [inputErrors, setInputErrors] = useState({});         // { [controlId]: string }

  const inputSchema = parseInputSchema(cmdDetail?.inputSchema);

  // Reset the picker's selections whenever a command with a structured file picker
  // is loaded fresh into Step 3 (keyed on the raw inputSchema string, which only
  // changes on an actual new GET /Command/{commandId} fetch — not on every render).
  useEffect(() => {
    if (!inputSchema) return;
    const init = {};
    inputSchema.controls.forEach(c => { init[c.id] = c.multiSelect ? [] : ''; });
    setInputSelections(init);
    setInputErrors({});
  }, [cmdDetail?.inputSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleExpandAll() {
    setTreeExpanded(v => {
      const next = !v;
      if (next) {
        const all = [];
        const skipped = [];
        collectFolderPaths(topNodes, '', all, skipped);
        setExpandedPaths(new Set(all));
        setSkippedFolders(skipped);
      } else {
        setExpandedPaths(new Set());
        setSkippedFolders([]);
      }
      return next;
    });
  }

  function toggleFolder(fullPath) {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(fullPath)) next.delete(fullPath); else next.add(fullPath);
      return next;
    });
  }

  // Fired by the Step 2 "Next" button: calls GET /v1/Command/{commandId} to fetch
  // the full command definition (description, inputSchema/outputSchema, …) for the
  // selected command, then advances to Step 3.
  async function handleNextFromCommand() {
    if (!selectedCmd || advancingToStep3) return;
    setAdvancingToStep3(true);
    setCmdDetail(null);
    try {
      const detail = await apiGetCommand(selectedCmd.id);
      setCmdDetail(detail);
    } catch (err) {
      console.error('Failed to load command detail:', err);
    } finally {
      setAdvancingToStep3(false);
      setStep(3);
    }
  }

  function loadTree() {
    setLoading(true);
    apiGetProjectSourceTree(project.id)
      .then(data => {
        setResourcePath(data?.resourcePath ?? '');
        setTree(data?.tree ?? null);
        // Show fully expanded once loaded — safe now that the tree is virtualized
        // (see VirtualSourceTree above), so this no longer means mounting every
        // node's DOM row up front, just marking every folder as "open".
        const all = [];
        const skipped = [];
        collectFolderPaths(topLevelNodesFromTree(data?.tree), '', all, skipped);
        setExpandedPaths(new Set(all));
        setSkippedFolders(skipped);
        setTreeExpanded(true);
      })
      .catch(() => {
        setResourcePath('');
        setTree(null);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadTree(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function togglePaths(paths) {
    onSelectionChange(prev => {
      const allSel = paths.every(p => prev.includes(p));
      if (allSel) return prev.filter(p => !paths.includes(p));
      return [...new Set([...prev, ...paths])];
    });
  }

  // Selects every file currently shown (respects the active search filter,
  // via visibleNodes) — adds to whatever's already selected rather than
  // replacing it, so switching folders/search terms doesn't lose a prior pick.
  function selectAllVisible() {
    const all = [];
    visibleNodes.forEach(n => collectPaths(n, '', all));
    onSelectionChange(prev => [...new Set([...prev, ...all])]);
  }

  function deselectAll() {
    onSelectionChange([]);
  }

  // Opens the confirm modal for a single row's trash icon — `node`/`path` identify
  // a file or a folder. The backend's delete endpoint deletes folders recursively
  // when given the folder's own path (see DELETE .../source-items), so a folder
  // is sent as that single path rather than expanded into its individual files —
  // expanding to leaf files only would delete the contents and leave the now-empty
  // folder behind on disk (and in the tree).
  function requestDeleteNode(node, path, name) {
    const full = path ? `${path}/${name}` : name;
    const count = isNodeFolder(node) ? countAllInNode(node) : 1;
    setDeleteError('');
    setConfirmDeleteTarget({ label: name, count, paths: [full] });
  }

  // Opens the confirm modal for the "Delete Selected" toolbar button. When every
  // file under a folder is selected, collapse those entries down to the folder's
  // own path so the delete actually removes the folder — not just its contents.
  function requestDeleteSelected() {
    if (!selectedFiles.length) return;
    const selSet = new Set(selectedFiles);
    const paths = [];
    collapseToDeletablePaths(topLevelNodesFromTree(tree), '', selSet, paths);
    setDeleteError('');
    setConfirmDeleteTarget({
      label: `${selectedFiles.length} selected item${selectedFiles.length !== 1 ? 's' : ''}`,
      count: selectedFiles.length,
      paths,
    });
  }

  // Opens the upload modal for a folder row's context menu — mode 'files' picks
  // individual files, 'folder' picks a whole folder (kept as a sub-folder).
  // The Source root row maps to an empty folderPath, which the API treats as
  // the project's Source folder itself.
  function requestUploadToFolder(node, path, name, mode = 'files') {
    setUploadFiles([]);
    setUploadFilesError('');
    const folderPath = node?.isSourceRoot ? '' : (path ? `${path}/${name}` : name);
    setUploadTarget({ folderPath, label: name, mode });
  }

  // Adds picked/dropped { file, path } entries, replacing any already-picked entry
  // with the same path.
  function addUploadEntries(entries) {
    if (!entries.length) return;
    setUploadFilesError('');
    setUploadFiles(prev => [
      ...prev.filter(u => !entries.some(n => n.path === u.path)),
      ...entries,
    ]);
  }

  // From an <input type="file">; a folder picker (webkitdirectory) fills in
  // webkitRelativePath ("MyFolder/sub/file.srw"), so the folder itself is kept.
  function addUploadFileList(fileList) {
    addUploadEntries(Array.from(fileList || []).map(file => ({
      file,
      path: file.webkitRelativePath || file.name,
    })));
  }

  // Drag-and-drop: walk any dropped folders so their files keep their sub-paths.
  async function addDroppedItems(dataTransfer) {
    const roots = Array.from(dataTransfer.items || [])
      .map(item => item.webkitGetAsEntry?.())
      .filter(Boolean);
    if (!roots.length) {
      addUploadFileList(dataTransfer.files);
      return;
    }
    const entries = [];
    async function walk(entry, prefix) {
      if (entry.isFile) {
        const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
        entries.push({ file, path: prefix + file.name });
        return;
      }
      const reader = entry.createReader();
      // readEntries returns results in batches — keep reading until it's empty.
      for (;;) {
        const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        if (!batch.length) break;
        for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
      }
    }
    try {
      for (const root of roots) await walk(root, '');
    } catch (err) {
      console.error('Reading dropped items failed:', err);
      setUploadFilesError('Some dropped items could not be read.');
    }
    addUploadEntries(entries);
  }

  function closeUploadModal() {
    if (uploadingFiles) return;
    setUploadTarget(null);
    setUploadFiles([]);
    setUploadDragOver(false);
  }

  async function handleConfirmUploadFiles() {
    if (!uploadTarget || !uploadFiles.length) return;
    setUploadingFiles(true);
    setUploadFilesError('');
    try {
      const result = await apiUploadFilesToFolder(project.id, uploadTarget.folderPath, uploadFiles);
      const items = result?.items ?? [];
      if ((result?.uploadedCount ?? 0) > 0) loadTree();
      const failed = items.filter(i => !i.uploaded);
      if (!failed.length) {
        setUploadTarget(null);
        setUploadFiles([]);
        return;
      }
      // Keep only the files that didn't make it, so the user can see why and
      // retry or remove them; the uploaded ones drop out of the list. A .zip
      // sent as-is reports its own entries, so the .zip itself is kept instead.
      if (!isZipUpload(uploadFiles)) {
        const failedNames = new Set(failed.map(i => i.fileName));
        setUploadFiles(prev => prev.filter(u => failedNames.has(u.path)));
      }
      const uploaded = items.length - failed.length;
      setUploadFilesError(
        `${uploaded ? `${uploaded} uploaded, ` : ''}${failed.length} failed:\n` +
        failed.map(i => `• ${i.fileName}: ${i.errorMessage || 'Not uploaded'}`).join('\n')
      );
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadFilesError(err.message || 'Failed to upload. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  }

  async function handleConfirmDeletePaths() {
    if (!confirmDeleteTarget) return;
    setDeletingPaths(true);
    setDeleteError('');
    try {
      await apiDeleteSourceFiles(project.id, confirmDeleteTarget.paths);
      // A deleted path may be a folder (selectedFiles holds individual files under
      // it, not the folder path itself), so drop anything that either matches
      // exactly or sits underneath a deleted folder — not just an exact match.
      onSelectionChange(prev => prev.filter(p =>
        !confirmDeleteTarget.paths.some(d => p === d || p.startsWith(`${d}/`))
      ));
      setConfirmDeleteTarget(null);
      loadTree();
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteError(err.message || 'Failed to delete. Please try again.');
    } finally {
      setDeletingPaths(false);
    }
  }

  function toggleInputSelection(control, path) {
    setInputSelections(prev => {
      // A file can only belong to one control's "type" at a time — the backend
      // tags it as source_code_type per file, not per (file, control) pair, so
      // letting the same file live under two controls would just mean whichever
      // control is processed last silently wins and the other tag is lost.
      // Guard at the source: picking a file already assigned to another control
      // is a no-op unless it's being unchecked from that same other control first.
      const takenByOther = inputSchema.controls.some(c =>
        c.id !== control.id &&
        (c.multiSelect ? (prev[c.id] ?? []).includes(path) : prev[c.id] === path)
      );
      if (takenByOther) return prev;

      if (control.multiSelect) {
        const cur = prev[control.id] ?? [];
        const next = cur.includes(path) ? cur.filter(p => p !== path) : [...cur, path];
        return { ...prev, [control.id]: next };
      }
      return { ...prev, [control.id]: path };
    });
  }

  // Step 2's fetched command detail may define a structured file picker rendered
  // inline on this page (inputSchema.controls) — Post validates that picker's
  // required fields before executing; commands without a schema execute directly.
  function handlePostClick() {
    if (!inputSchema) { handleExecute(); return; }

    const errors = {};
    inputSchema.controls.forEach(c => {
      const val = inputSelections[c.id];
      const empty = c.multiSelect ? !(val?.length) : !val;
      if (c.required && empty) errors[c.id] = 'Select at least one file for this field.';
    });
    if (Object.keys(errors).length) { setInputErrors(errors); return; }

    // Tag every selected file with the control.type it was placed under; anything
    // picked in Step 1 but not assigned to any control falls back to "source code"
    // (matches how the backend has historically tagged untyped files).
    const typeMap = {};
    inputSchema.controls.forEach(c => {
      const val = inputSelections[c.id];
      (c.multiSelect ? val : val ? [val] : []).forEach(path => { typeMap[path] = c.type; });
    });
    handleExecute(typeMap);
  }

  async function handleExecute(typeMap = {}) {
    if (!selectedCmd || submitting) return;
    setSubmitting(true);
    try {
      const files = buildSourceFileList(resourcePath, selectedFiles, typeMap);
      const sourceFileInfo = files.length ? JSON.stringify(files) : null;
      const queued = await apiAddToCommandQueue({
        fkCommandId:    selectedCmd.id,
        fkProjectId:    project.id,
        sourceFileInfo,
        userId:         user?.id ?? 0,
      });
      const task = {
        id:          queued.commandQueueId ?? Date.now(),
        commandId:   selectedCmd.id,
        commandName: selectedCmd.name,
        status:      queued.queueStatus ?? 'QUEUED',
        files:       files.length ? files : null,
        fileCount:   selectedFiles.length || null,
        queuedBy:    user?.name ?? '—',
        createdBy:   queued.createdBy ?? user?.id ?? 0,
        createdAt:   queued.createdAt ?? new Date().toISOString(),
        completedAt: queued.completedAt ?? null,
        errorDetails: null, output: null, outputId: null,
      };
      onTaskCreated(task);
      setLastQueued(task.id);
      onExecuteSuccess?.();
    } catch (err) {
      console.error('Execute failed:', err);
    } finally {
      setSubmitting(false);
    }
  }

  // Memoized so an unrelated re-render of this panel (task-queue polling, hover
  // state, a keystroke in the command search box, …) doesn't rebuild these —
  // topNodes/visibleNodes feed VirtualSourceTree's own flatten pass, and
  // recomputing that from scratch on every render was a major source of the
  // sustained allocation churn that ran the tab out of memory on large trees.
  const topNodes = useMemo(() => topLevelNodesFromTree(tree), [tree]);

  // The API always returns the project's Source folder as the tree root — kept
  // as a visible parent row so files/folders can be uploaded into it even when
  // the project has no source yet.
  const rootName = (tree && !Array.isArray(tree) && tree.name) || 'Source';

  const visibleNodes = useMemo(
    () => filterTreeNodes(topNodes, fileSearch.trim()),
    [topNodes, fileSearch]
  );

  return (
    <div style={SW.wizard}>

      {/* ── Step progress bar ── */}
      <div style={SW.stepBar}>
        <div style={SW.stepTrack}>
          {WIZARD_STEPS.map((s, i) => {
            const done   = step > s.id;
            const active = step === s.id;
            return [
              <div key={`step-${s.id}`} style={SW.stepItem}>
                <div style={{ ...SW.stepCircle, ...(active ? SW.stepCircleActive : done ? SW.stepCircleDone : {}) }}>
                  {done ? '✓' : s.id}
                </div>
                <span style={{ ...SW.stepLabel, ...(active ? SW.stepLabelActive : done ? SW.stepLabelDone : {}) }}>
                  {s.label}
                </span>
              </div>,
              i < WIZARD_STEPS.length - 1
                ? <div key={`line-${s.id}`} style={{ ...SW.stepConnector, ...(done ? SW.stepConnectorDone : {}) }} />
                : null,
            ];
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          STEP 1 — Select Source Files
          ══════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div style={SW.stepBody}>
          <div style={SW.stepBanner}>
            <div style={SW.stepBannerNum}>1</div>
            <div style={SW.stepBannerText}>
              <span style={SW.stepBannerTitle}>Select Source Files</span>
              <span style={SW.stepBannerSub}>Select files to continue.</span>
            </div>
            {selectedFiles.length > 0 && (
              <span style={SW.stepBannerBadge}>
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>

          <div style={SW.searchBar}>
            <span style={SW.searchIcon}>&#128269;</span>
            <input
              style={SW.searchInput}
              type="text"
              autoComplete="off"
              placeholder="Search files and folders…"
              value={fileSearch}
              onChange={e => setFileSearch(e.target.value)}
            />
            {fileSearch && (
              <button style={SW.searchClear} onClick={() => setFileSearch('')}>✕</button>
            )}
            <button style={SW.expandAllBtn} onClick={selectAllVisible} title="Select every file currently shown">
              ☑ Select All
            </button>
            <button style={SW.expandAllBtn} onClick={deselectAll} title="Clear all selected files">
              ☐ Deselect All
            </button>
            <button
              style={SW.expandAllBtn}
              onClick={toggleExpandAll}
              title={treeExpanded ? 'Collapse all folders' : 'Expand all folders'}
            >
              {treeExpanded ? '⊟ Collapse All' : '⊞ Expand All'}
            </button>
            <button
              style={{
                ...SW.expandAllBtn,
                ...(selectedFiles.length > 0 ? SW.deleteSelectedBtnActive : SW.deleteSelectedBtnDisabled),
              }}
              onClick={requestDeleteSelected}
              disabled={selectedFiles.length === 0}
              title="Delete every currently-selected file"
            >
              🗑 Delete Selected
            </button>
            {treeExpanded && skippedFolders.length > 0 && (
              <span
                style={SW.expandAllWarning}
                title={`Too large to auto-expand — open individually:\n${skippedFolders.join('\n')}`}
              >
                ⚠ {skippedFolders.length} large folder{skippedFolders.length !== 1 ? 's' : ''} left collapsed
              </span>
            )}
          </div>

          <div style={{ ...SW.stepScroll, overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading && <PageLoader label="Loading source tree…" minHeight={200} />}
            {!loading && fileSearch.trim() && visibleNodes.length === 0 && (
              <div style={S.placeholder}>No files match "{fileSearch}".</div>
            )}
            {!loading && !(fileSearch.trim() && visibleNodes.length === 0) && (
              <VirtualSourceTree
                rootName={rootName}
                nodes={visibleNodes}
                selectedFiles={selectedFiles}
                onTogglePaths={togglePaths}
                expandedPaths={expandedPaths}
                onToggleFolder={toggleFolder}
                forceExpand={!!fileSearch.trim()}
                onRequestDelete={requestDeleteNode}
                onRequestUpload={requestUploadToFolder}
              />
            )}
            {!loading && topNodes.length === 0 && (
              <div style={{ ...SW.footerHint, padding: '10px 16px' }}>Right-click "{rootName}" to upload files or a folder.</div>
            )}
          </div>

          <div style={SW.footer}>
            <span style={SW.footerHint}>
              {selectedFiles.length > 0
                ? `${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''} selected.`
                : 'Select at least one file to continue.'}
            </span>
            <button
              style={{ ...SW.nextBtn, opacity: selectedFiles.length > 0 ? 1 : 0.4, cursor: selectedFiles.length > 0 ? 'pointer' : 'not-allowed' }}
              disabled={selectedFiles.length === 0}
              onClick={() => setStep(2)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          STEP 2 — Select Command
          ══════════════════════════════════════════════════════ */}
      {step === 2 && (
        <div style={SW.stepBody}>
          <div style={SW.stepBanner}>
            <div style={SW.stepBannerNum}>2</div>
            <div style={SW.stepBannerText}>
              <span style={SW.stepBannerTitle}>Select a Command</span>
              <span style={SW.stepBannerSub}>Choose one command to run on your selected source.</span>
            </div>
            {selectedCmd && (
              <span style={SW.stepBannerBadge}>{selectedCmd.name}</span>
            )}
          </div>

          <div style={SW.searchBar}>
            <span style={SW.searchIcon}>&#128269;</span>
            <input
              style={SW.searchInput}
              type="text"
              autoComplete="off"
              placeholder="Search commands..."
              value={cmdSearch}
              onChange={e => setCmdSearch(e.target.value)}
            />
            {cmdSearch && (
              <button style={SW.searchClear} onClick={() => setCmdSearch('')}>✕</button>
            )}
          </div>

          <div style={SW.stepScroll}>
            <div style={SW.cmdGrid}>
              {(() => {
                const q = cmdSearch.trim().toLowerCase();
                const filtered = q
                  ? commands.filter(cmd =>
                      cmd.name.toLowerCase().includes(q) ||
                      cmd.description.toLowerCase().includes(q))
                  : commands;
                if (commands.length === 0) return (
                  <div style={S.placeholder}>No commands assigned to this project.</div>
                );
                if (filtered.length === 0) return (
                  <div style={S.placeholder}>No commands match "{cmdSearch}".</div>
                );
                return filtered.map(cmd => {
                  const isSelected = selectedCmd?.id === cmd.id;
                  const isHovered  = hoveredCmd === cmd.id;
                  return (
                    <button
                      key={cmd.id}
                      style={{ ...SW.cmdCard, ...(isSelected ? SW.cmdCardSelected : isHovered ? SW.cmdCardHover : {}) }}
                      onMouseEnter={() => setHoveredCmd(cmd.id)}
                      onMouseLeave={() => setHoveredCmd(null)}
                      onClick={() => setSelectedCmd(cmd)}
                    >
                      <div style={{ ...SW.cmdNum, ...(isSelected ? SW.cmdNumSelected : isHovered ? SW.cmdNumHover : {}) }}>
                        {isSelected ? '✓' : cmd.order}
                      </div>
                      <div style={SW.cmdInfo}>
                        <span style={SW.cmdName}>{cmd.name}</span>
                        <span style={SW.cmdDesc}>{cmd.description}</span>
                      </div>
                      {isSelected
                        ? <span style={SW.cmdCheck}>✓ Selected</span>
                        : <span style={{ ...SW.cmdArrow, ...(isHovered ? SW.cmdArrowHover : {}) }}>→</span>
                      }
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          <div style={SW.footer}>
            <button style={SW.backBtn} onClick={() => setStep(1)}>← Back</button>
            <span style={SW.footerHint}>
              {selectedCmd ? `"${selectedCmd.name}" selected.` : 'Select a command to continue.'}
            </span>
            <button
              style={{ ...SW.nextBtn, opacity: selectedCmd ? 1 : 0.4, cursor: selectedCmd ? 'pointer' : 'not-allowed' }}
              disabled={!selectedCmd || advancingToStep3}
              onClick={handleNextFromCommand}
            >
              {advancingToStep3 ? '⏳ …' : 'Next →'}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          STEP 3 — Command Queue
          ══════════════════════════════════════════════════════ */}
      {step === 3 && (
        <div style={SW.stepBody}>
          <div style={SW.stepBanner}>
            <div style={SW.stepBannerNum}>3</div>
            <div style={SW.stepBannerText}>
              <span style={SW.stepBannerTitle}>Task Queue</span>
              <span style={SW.stepBannerSub}>Review your selection and execute the command.</span>
            </div>
          </div>

          <div style={SW.stepScroll}>
            {/* Selection review */}
            <div style={SW.reviewCard}>
              <div style={SW.reviewItem}>
                <span style={SW.reviewIcon}>📂</span>
                <div style={SW.reviewText}>
                  <span style={SW.reviewLabel}>Source</span>
                  <span style={SW.reviewVal}>
                    {selectedFiles.length > 0
                      ? `${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''} selected`
                      : 'Full project'}
                  </span>
                </div>
                <button style={SW.reviewEdit} onClick={() => setStep(1)}>Change</button>
              </div>
              <div style={SW.reviewDivider} />
              <div style={SW.reviewItem}>
                <span style={SW.reviewIcon}>⚙️</span>
                <div style={SW.reviewText}>
                  <span style={SW.reviewLabel}>Command</span>
                  <span style={SW.reviewVal}>{selectedCmd?.name ?? '—'}</span>
                </div>
                <button style={SW.reviewEdit} onClick={() => setStep(2)}>Change</button>
              </div>
            </div>

            {lastQueued && (
              <div style={SW.successBanner}>✓ Command added to queue successfully.</div>
            )}

            {/* Structured file picker — rendered on the page itself (not a popup),
                driven by the selected command's inputSchema.controls: radio for
                multiSelect:false, checkbox for multiSelect:true. Placed last since
                it can get tall once a command's source list has many files. */}
            {inputSchema && (
              <CommandInputFields
                schema={inputSchema}
                files={selectedFiles}
                selections={inputSelections}
                errors={inputErrors}
                onToggle={toggleInputSelection}
              />
            )}
          </div>

          <div style={SW.footer}>
            <button style={SW.backBtn} onClick={() => setStep(2)}>← Back</button>
            <span style={SW.footerHint} />
            <button
              style={{ ...SW.submitBtn, opacity: (submitting || !selectedCmd) ? 0.5 : 1 }}
              disabled={submitting || !selectedCmd}
              onClick={handlePostClick}
            >
              {submitting ? '⏳ Posting…' : '▶ Post'}
            </button>
          </div>
        </div>
      )}

      {/* ── Upload files/folder into a folder (right-click → Upload files/folder here) ── */}
      {uploadTarget && (
        <div style={S.modalOverlay} onClick={closeUploadModal}>
          <div style={{ ...S.modalCard, width: 460 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{uploadTarget.mode === 'folder' ? 'Upload folder' : 'Upload files'}</div>
            <div style={S.modalBody}>
              {uploadTarget.mode === 'folder'
                ? <>The selected folder will be added inside <strong title={uploadTarget.folderPath || uploadTarget.label}>{uploadTarget.folderPath || uploadTarget.label}</strong>, keeping its sub-folders.</>
                : <>Files will be added to <strong title={uploadTarget.folderPath || uploadTarget.label}>{uploadTarget.folderPath || uploadTarget.label}</strong>.</>}
              {' '}A file with the same name already there will be overwritten.
            </div>
            <div
              style={{ ...S.uploadDropZone, ...(uploadDragOver ? S.uploadDropZoneActive : {}) }}
              onClick={() => !uploadingFiles && uploadFilesRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setUploadDragOver(true); }}
              onDragLeave={() => setUploadDragOver(false)}
              onDrop={e => {
                e.preventDefault();
                setUploadDragOver(false);
                if (!uploadingFiles) addDroppedItems(e.dataTransfer);
              }}
            >
              <div style={{ fontSize: 22 }}>{uploadTarget.mode === 'folder' ? '📁' : '📤'}</div>
              <div style={{ fontWeight: 600 }}>
                {uploadTarget.mode === 'folder' ? 'Drop a folder here or click to browse' : 'Drop files here or click to browse'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {uploadTarget.mode === 'folder' ? 'All files inside it, including sub-folders, are uploaded' : 'You can select multiple files'}
              </div>
              {/* Keyed on mode so React remounts the input — the webkitdirectory
                  attribute doesn't reliably toggle on an existing element. */}
              <input
                key={uploadTarget.mode}
                ref={uploadFilesRef}
                type="file"
                multiple
                {...(uploadTarget.mode === 'folder' ? { webkitdirectory: '', directory: '' } : {})}
                style={{ display: 'none' }}
                onChange={e => { addUploadFileList(e.target.files); e.target.value = ''; }}
              />
            </div>
            {uploadFiles.length > 0 && (
              <div style={S.uploadFileList}>
                {uploadFiles.map(u => (
                  <div key={u.path} style={S.uploadFileItem}>
                    <span style={{ fontSize: 13, flexShrink: 0 }}>{fileIcon(u.file.name)}</span>
                    <span style={S.uploadFileName} title={u.path}>{u.path}</span>
                    <span style={S.uploadFileSize}>{formatFileSize(u.file.size)}</span>
                    <button
                      style={S.uploadFileRemoveBtn}
                      disabled={uploadingFiles}
                      onClick={() => setUploadFiles(prev => prev.filter(x => x !== u))}
                      title={`Remove "${u.path}"`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            {uploadFilesError && <div style={{ color: '#f85149', fontSize: 13, fontWeight: 600, whiteSpace: 'pre-wrap' }}>{uploadFilesError}</div>}
            <div style={S.modalActions}>
              <button style={S.modalCancelBtn} disabled={uploadingFiles} onClick={closeUploadModal}>
                Cancel
              </button>
              <button
                style={{ ...S.modalPrimaryBtn, opacity: uploadFiles.length ? 1 : 0.4, cursor: uploadFiles.length ? 'pointer' : 'not-allowed' }}
                disabled={uploadingFiles || !uploadFiles.length}
                onClick={handleConfirmUploadFiles}
              >
                {uploadingFiles
                  ? 'Uploading…'
                  : `Upload${uploadFiles.length ? ` ${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''}` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete file(s)/folder(s) confirmation (FPB-70) ── */}
      {confirmDeleteTarget && (
        <div style={S.modalOverlay} onClick={() => !deletingPaths && setConfirmDeleteTarget(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>Delete from source?</div>
            <div style={S.modalBody}>
              This will permanently remove <strong>{confirmDeleteTarget.label}</strong>
              {confirmDeleteTarget.count > 1 ? ` (${confirmDeleteTarget.count} files)` : ''} from the project's
              source. This action cannot be undone.
              {deleteError && <div style={{ color: '#f85149', marginTop: 8, fontWeight: 600 }}>{deleteError}</div>}
            </div>
            <div style={S.modalActions}>
              <button style={S.modalCancelBtn} disabled={deletingPaths} onClick={() => setConfirmDeleteTarget(null)}>
                Cancel
              </button>
              <button style={S.modalDeleteBtn} disabled={deletingPaths} onClick={handleConfirmDeletePaths}>
                {deletingPaths ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Renders a command's inputSchema directly on the page (no popup): one section per
// control, listing the files already selected in Step 1 as radio options
// (multiSelect: false) or checkbox options (multiSelect: true), with inline
// "select at least one" validation for controls marked required.
function CommandInputFields({ schema, files, selections, errors, onToggle }) {
  return (
    <div style={IM.card}>
      <span style={IM.title}>{schema.popupTitle ?? 'Select Files'}</span>

      <div style={IM.body}>
        {schema.controls.map(control => {
            const val = selections[control.id];
            const selectedSet = control.multiSelect ? new Set(val ?? []) : null;
            return (
              <div key={control.id} style={IM.field}>
                <div style={IM.fieldHeader}>
                  <span style={IM.fieldLabel}>
                    {control.text}
                    {control.required && <span style={IM.required}> *</span>}
                  </span>
                  <span style={IM.fieldHint}>{control.multiSelect ? 'Select one or more' : 'Select one'}</span>
                </div>

                <div style={IM.fileList}>
                  {files.length === 0 && (
                    <div style={IM.fileListEmpty}>No files selected in Step 1.</div>
                  )}
                  {files.map(path => {
                    const name = path.split('/').pop();
                    const checked = control.multiSelect ? selectedSet.has(path) : val === path;
                    // A file already assigned to a different control can't also be
                    // picked here — each file gets exactly one "type" tag downstream.
                    const takenByControl = schema.controls.find(c =>
                      c.id !== control.id &&
                      (c.multiSelect ? (selections[c.id] ?? []).includes(path) : selections[c.id] === path)
                    );
                    return (
                      <label
                        key={path}
                        style={{ ...IM.fileRow, ...(takenByControl ? IM.fileRowDisabled : {}) }}
                        title={takenByControl ? `Already selected for "${takenByControl.text}"` : undefined}
                      >
                        <input
                          type={control.multiSelect ? 'checkbox' : 'radio'}
                          name={control.multiSelect ? undefined : `field-${control.id}`}
                          checked={checked}
                          disabled={!!takenByControl}
                          onChange={() => onToggle(control, path)}
                          style={IM.fileCheckbox}
                        />
                        {name}
                        {takenByControl && (
                          <span style={IM.fileRowNote}>— already used for "{takenByControl.text}"</span>
                        )}
                      </label>
                    );
                  })}
                </div>

                {errors[control.id] && (
                  <div style={IM.fieldError}>{errors[control.id]}</div>
                )}
              </div>
            );
        })}
      </div>
    </div>
  );
}

// ── Virtualized source tree ───────────────────────────────────────────────────
// A folder can hold tens of thousands of entries (e.g. an extracted MACOSX/
// __MACOSX metadata folder). The old version mounted one <SourceTreeEx> per
// node, recursively — opening one big folder meant mounting that many real DOM
// rows in one go, which is what made expanding it slow. This flattens the
// currently-*visible* rows (respecting which folders are open) into a plain
// array and hands it to react-window's <List>, which only ever mounts however
// many rows fit on screen, however large the underlying folder is.
const TREE_ROW_HEIGHT = 28;

function flattenVisibleTreeRows(nodes, path, depth, isOpen, out) {
  sortNodes(nodes).forEach(node => {
    const name = node.name ?? node.fileName ?? node.resourceText ?? '?';
    const fullPath = path ? `${path}/${name}` : name;
    const isFolder = isNodeFolder(node);
    out.push({ node, name, path, fullPath, depth, isFolder });
    if (isFolder && isOpen(fullPath)) {
      flattenVisibleTreeRows(node.children ?? node.files ?? node.items ?? [], fullPath, depth + 1, isOpen, out);
    }
  });
}

// Raw tree payload can be an array of top-level nodes, or a single root node
// carrying them under children/files/items — normalize to a sorted array.
function topLevelNodesFromTree(tree) {
  if (!tree) return [];
  return sortNodes(Array.isArray(tree) ? tree : (tree.children ?? tree.files ?? tree.items ?? [tree]));
}

function filterTreeNodes(nodes, q) {
  if (!q) return nodes;
  const lower = q.toLowerCase();
  function filterNode(node) {
    const name = (node.name ?? node.fileName ?? '').toLowerCase();
    if (name.includes(lower)) return node;
    if (node.children?.length) {
      const filteredChildren = node.children.map(filterNode).filter(Boolean);
      if (filteredChildren.length) return { ...node, children: filteredChildren };
    }
    return null;
  }
  return nodes.map(filterNode).filter(Boolean);
}

// Folders bigger than this stay collapsed when bulk-expanding (initial load, or
// the "Expand All" button). This used to be a low 500-item cap because
// recursively expanding EVERY folder re-flattened the whole tree on every
// subsequent render (task-queue polling, hover states, etc. all re-render
// this panel) — sustained allocation over 15+ minutes that ran the tab out of
// memory. That's now fixed at the source: VirtualSourceTree's flatten pass is
// memoized on [nodes, expandedPaths, forceExpand] (see useMemo below), so a
// poll/hover/keystroke re-render no longer redoes the flatten — it only runs
// when expandedPaths actually changes. React-window also still only ever
// mounts however many DOM rows fit on screen, regardless of this number. So
// this is now just a defensive ceiling against a truly absurd tree (millions
// of entries), not the primary guard — real project folders should expand
// freely. A folder past even this can still be opened deliberately, one click
// at a time — see the per-folder chevron toggle instead.
const AUTO_EXPAND_MAX_ITEMS = 100000;

// Used by "Expand All" (and to auto-expand a freshly-loaded tree) to seed
// expandedPaths with every folder path up front, skipping oversized folders.
// `skipped`, if passed, collects the full path of every folder left collapsed
// so callers can tell the user *why* Expand All didn't open everything —
// without it, hitting the cap looks indistinguishable from the button doing
// nothing at all.
function collectFolderPaths(nodes, path, out, skipped) {
  sortNodes(nodes).forEach(node => {
    if (!isNodeFolder(node)) return;
    const name = node.name ?? node.fileName ?? '?';
    const full = path ? `${path}/${name}` : name;
    if (countAllInNode(node) > AUTO_EXPAND_MAX_ITEMS) {
      skipped?.push(full);
      return; // leave collapsed
    }
    out.push(full);
    collectFolderPaths(node.children ?? node.files ?? node.items ?? [], full, out, skipped);
  });
}

// Stable module-level component (not re-created per render) so react-window
// doesn't remount every visible row just because, say, a checkbox was toggled.
function SourceTreeRow({ index, style, rows, selectedFiles, onTogglePaths, expandedPaths, forceExpand, onToggleFolder, onOpenContextMenu }) {
  const row = rows[index];
  const { node, name, path, fullPath, depth, isFolder } = row;
  const pbType = pbTypeFor(name);
  const rowStyle = { ...style, ...SX.treeRow, margin: 0, paddingLeft: 6 + depth * 18 };

  // Right-click opens the row's context menu (Delete) instead of the browser's.
  function onContextMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    onOpenContextMenu?.(e.clientX, e.clientY, { node, path, name, isFolder, isSourceRoot: !!row.isSourceRoot });
  }

  // The Source root row: always open, and its children's paths don't include
  // its own name (selection/delete paths stay relative to Source).
  if (row.isSourceRoot) {
    const children   = node.children;
    const selCount   = children.reduce((s, c) => s + countSelInNode(c, '', selectedFiles), 0);
    const totalCount = children.reduce((s, c) => s + countAllInNode(c), 0);
    const allChecked = totalCount > 0 && selCount === totalCount;
    const partial    = selCount > 0 && selCount < totalCount;

    function onRootToggle(e) {
      e.stopPropagation();
      const paths = [];
      children.forEach(c => collectPaths(c, '', paths));
      if (paths.length) onTogglePaths(paths);
    }

    return (
      <div style={rowStyle} onContextMenu={onContextMenu}>
        <input
          type="checkbox"
          checked={allChecked}
          disabled={totalCount === 0}
          ref={el => { if (el) el.indeterminate = partial; }}
          onChange={onRootToggle}
          style={SX.checkbox}
          onClick={e => e.stopPropagation()}
        />
        <span style={SX.chevron}>▾</span>
        <span style={{ fontSize: 14, flexShrink: 0 }}>📂</span>
        <span style={SX.folderName}>{name}</span>
        <span style={SX.folderCount}>
          {selCount > 0 ? `${selCount}/` : ''}{totalCount}
        </span>
      </div>
    );
  }

  if (isFolder) {
    const selCount   = countSelInNode(node, path, selectedFiles);
    const totalCount = countAllInNode(node);
    const allChecked = totalCount > 0 && selCount === totalCount;
    const partial    = selCount > 0 && selCount < totalCount;
    const isOpen      = forceExpand || expandedPaths.has(fullPath);

    function onFolderToggle(e) {
      e.stopPropagation();
      const paths = [];
      collectPaths(node, path, paths);
      onTogglePaths(paths);
    }

    return (
      <div style={rowStyle} onContextMenu={onContextMenu}>
        <input
          type="checkbox"
          checked={allChecked}
          ref={el => { if (el) el.indeterminate = partial; }}
          onChange={onFolderToggle}
          style={SX.checkbox}
          onClick={e => e.stopPropagation()}
        />
        <span style={SX.chevron} onClick={() => onToggleFolder(fullPath)}>{isOpen ? '▾' : '▸'}</span>
        <span style={{ fontSize: 14, flexShrink: 0, cursor: 'pointer' }} onClick={() => onToggleFolder(fullPath)} title={pbType?.label}>
          {pbType ? pbType.icon : (isOpen ? '📂' : '📁')}
        </span>
        <span style={SX.folderName} onClick={() => onToggleFolder(fullPath)}>{name}</span>
        <span style={SX.folderCount}>
          {selCount > 0 ? `${selCount}/` : ''}{totalCount}
        </span>
      </div>
    );
  }

  const checked = selectedFiles.includes(fullPath);

  return (
    <div
      style={{ ...rowStyle, ...(checked ? SX.treeRowChecked : {}) }}
      onClick={() => onTogglePaths([fullPath])}
      onContextMenu={onContextMenu}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onTogglePaths([fullPath])}
        style={SX.checkbox}
        onClick={e => e.stopPropagation()}
      />
      <span style={{ fontSize: 13, flexShrink: 0 }} title={pbType?.label}>{fileIcon(name)}</span>
      <span style={SX.fileName}>{name}</span>
    </div>
  );
}

// With `rootName`, a fixed root folder row (the project's Source folder) is
// shown above `nodes`, which render indented beneath it.
function VirtualSourceTree({ rootName, nodes, selectedFiles, onTogglePaths, expandedPaths, onToggleFolder, forceExpand, onRequestDelete, onRequestUpload }) {
  const rows = useMemo(() => {
    const out = [];
    if (rootName) {
      const rootNode = { name: rootName, nodeType: 'Folder', isSourceRoot: true, children: nodes };
      out.push({ node: rootNode, name: rootName, path: '', fullPath: '', depth: 0, isFolder: true, isSourceRoot: true });
    }
    flattenVisibleTreeRows(nodes, '', rootName ? 1 : 0, fullPath => forceExpand || expandedPaths.has(fullPath), out);
    return out;
  }, [rootName, nodes, expandedPaths, forceExpand]);

  // Right-click context menu — null | { x, y, target: { node, path, name, isFolder } }.
  const [contextMenu, setContextMenu] = useState(null);

  // Stable so rowProps (and therefore every mounted row) isn't rebuilt on open/close.
  const onOpenContextMenu = useMemo(() => (x, y, target) => setContextMenu({ x, y, target }), []);

  // Any click, scroll, resize or Escape elsewhere dismisses the menu.
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const onKey = e => { if (e.key === 'Escape') close(); };
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const rowProps = useMemo(
    () => ({ rows, selectedFiles, onTogglePaths, expandedPaths, forceExpand, onToggleFolder, onOpenContextMenu }),
    [rows, selectedFiles, onTogglePaths, expandedPaths, forceExpand, onToggleFolder, onOpenContextMenu]
  );

  function onUploadMenuClick(mode) {
    const { node, path, name } = contextMenu.target;
    setContextMenu(null);
    onRequestUpload?.(node, path, name, mode);
  }

  function onDeleteMenuClick() {
    const { node, path, name } = contextMenu.target;
    setContextMenu(null);
    onRequestDelete?.(node, path, name);
  }

  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <List
        rowComponent={SourceTreeRow}
        rowCount={rows.length}
        rowHeight={TREE_ROW_HEIGHT}
        rowProps={rowProps}
        style={{ height: '100%' }}
      />
      {contextMenu && (
        <div
          style={{
            ...SX.contextMenu,
            // Keep the menu on-screen when opened near the right/bottom edge.
            left: Math.min(contextMenu.x, window.innerWidth - 200),
            top: Math.min(contextMenu.y, window.innerHeight - (contextMenu.target.isFolder ? 110 : 50)),
          }}
          onMouseDown={e => e.stopPropagation()}
          onContextMenu={e => e.preventDefault()}
        >
          {contextMenu.target.isFolder && onRequestUpload && (
            <>
              <button
                style={{ ...SX.contextMenuItem, color: 'var(--text-primary)' }}
                onClick={() => onUploadMenuClick('files')}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                title={`Upload files into "${contextMenu.target.name}"`}
              >
                📤 Upload files here
              </button>
              <button
                style={{ ...SX.contextMenuItem, color: 'var(--text-primary)' }}
                onClick={() => onUploadMenuClick('folder')}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                title={`Upload a folder into "${contextMenu.target.name}"`}
              >
                📁 Upload folder here
              </button>
            </>
          )}
          {!contextMenu.target.isSourceRoot && (
            <button
              style={SX.contextMenuItem}
              onClick={onDeleteMenuClick}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,81,73,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              title={contextMenu.target.isFolder ? `Delete "${contextMenu.target.name}" and everything in it` : `Delete "${contextMenu.target.name}"`}
            >
              🗑 Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tasks panel ───────────────────────────────────────────────────────────────

const STATUS_CFG = {
  QUEUED:     { label: 'Queued',     color: '#8b949e', bg: 'rgba(139,148,158,0.12)', icon: '⏳' },
  PROCESSING: { label: 'Processing', color: '#58a6ff', bg: 'rgba(88,166,255,0.12)',  icon: '⚙️' },
  PROCESSED:  { label: 'Processed',  color: '#3fb950', bg: 'rgba(63,185,80,0.12)',   icon: '✅' },
  FAILED:     { label: 'Failed',     color: '#f85149', bg: 'rgba(248,81,73,0.12)',   icon: '❌' },
};

const TASKS_PAGE_SIZE = 10;

// ProcessingNotes is an append-only log of "[<utc>] <message>" lines; the last
// line of a FAILED task is the failure reason.
function parseProcessingNotes(notes) {
  if (!notes) return [];
  return notes.split(/\r?\n/).filter(l => l.trim()).map(line => {
    const m = line.match(/^\[([^\]]+)\]\s*(.*)$/);
    return m ? { at: m[1], message: m[2] } : { at: null, message: line };
  });
}

function taskErrorSummary(task) {
  const entries = parseProcessingNotes(task.errorDetails);
  return entries.length ? entries[entries.length - 1].message : null;
}

function TasksPanel({ tasks, user, jumpToId, onJumpClear, onViewResult, onRequestDelete, onRefresh, refreshing, loading, userFilter, onUserFilterChange, userOptions }) {
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [downloadingId, setDownloadingId] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (jumpToId != null) {
      setExpandedId(jumpToId);
      onJumpClear();
    }
  }, [jumpToId, onJumpClear]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortKey, sortDir, userFilter]);

  async function handleDownload(task) {
    if (downloadingId) return;
    setDownloadingId(task.id);
    try {
      const { blob, filename } = await apiDownloadGeneratedCode(task.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  }

  const q = search.trim().toLowerCase();
  // Status tab counts reflect the chosen user so the badges match the list.
  const userTasks = tasks.filter(t => matchesUser(t, userFilter));
  const visibleTasks = userTasks
    .filter(t => statusFilter === 'all' || t.status === statusFilter)
    .filter(t => !q || (t.commandName ?? '').toLowerCase().includes(q))
    .slice()
    .sort((a, b) => {
      const av = apiTimestampToDate(a[sortKey] ?? a.createdAt)?.getTime() ?? 0;
      const bv = apiTimestampToDate(b[sortKey] ?? b.createdAt)?.getTime() ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const statusTabs = [
    { key: 'all', label: 'All', count: userTasks.length },
    ...Object.entries(STATUS_CFG).map(([key, cfg]) => ({
      key, label: cfg.label, count: userTasks.filter(t => t.status === key).length,
    })),
  ];

  const totalPages = Math.max(1, Math.ceil(visibleTasks.length / TASKS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedTasks = visibleTasks.slice(
    (currentPage - 1) * TASKS_PAGE_SIZE,
    currentPage * TASKS_PAGE_SIZE
  );

  return (
    <div style={S.panelWrap}>
      <div style={S.panelHeader}>
        <span style={S.panelTitle}>⚡ Tasks</span>
        <span style={S.panelSub}>{visibleTasks.length} task{visibleTasks.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={S.taskToolbar}>
        <input
          type="text"
          autoComplete="off"
          placeholder="Search by command…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={S.taskSearchInput}
        />
        <div style={S.taskTabs}>
          {statusTabs.map(t => (
            <button
              key={t.key}
              style={{ ...S.taskTab, ...(statusFilter === t.key ? S.taskTabActive : {}) }}
              onClick={() => setStatusFilter(t.key)}
            >
              {t.label}
              <span style={{ ...S.taskTabBadge, ...(statusFilter === t.key ? S.taskTabBadgeActive : {}) }}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
        <div style={S.taskFilterGroup}>
          <button
            style={S.refreshBtn}
            title="Refresh tasks"
            disabled={refreshing}
            onClick={onRefresh}
          >{refreshing ? '⏳' : '⟳'} Refresh</button>
          <UserFilterSelect value={userFilter} onChange={onUserFilterChange} options={userOptions} />
          <span style={S.taskFilterLabel}>Sort by</span>
          <select style={S.taskSelect} value={sortKey} onChange={e => setSortKey(e.target.value)}>
            <option value="createdAt">Created date</option>
            <option value="updatedAt">Updated date</option>
          </select>
          <button
            style={S.sortDirBtn}
            title="Toggle sort direction"
            onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
          >
            {sortDir === 'desc' ? '↓ Newest first' : '↑ Oldest first'}
          </button>
        </div>
      </div>

      <div style={S.scrollArea}>
        {loading && <PageLoader label="Loading tasks…" minHeight={200} />}
        {!loading && visibleTasks.length === 0 && (
          <div style={S.placeholder}>
            {tasks.length === 0 ? 'No tasks yet. Run a command to create one.' : 'No tasks match the current filter.'}
          </div>
        )}
        {!loading && pagedTasks.map(task => {
          const cfg = STATUS_CFG[task.status] ?? STATUS_CFG.QUEUED;
          const expanded = expandedId === task.id;
          return (
            <div key={task.id} style={{ ...S.taskCard, ...(expanded ? S.taskCardOpen : {}) }}>
              <div
                style={S.taskCardHeader}
                onClick={() => setExpandedId(expanded ? null : task.id)}
              >
                <span style={S.taskId}>#{task.id}</span>
                <span style={{ ...S.statusPill, color: cfg.color, background: cfg.bg }}>
                  {cfg.icon} {cfg.label}
                </span>
                <div style={S.taskInfo}>
                  <span style={S.taskTitle}>{task.title}</span>
                  {task.commandName && (
                    <span style={S.taskCmd}>{task.commandName}</span>
                  )}
                  {task.status === 'FAILED' && !expanded && taskErrorSummary(task) && (
                    <span style={S.taskErrorInline} title={taskErrorSummary(task)}>
                      {taskErrorSummary(task)}
                    </span>
                  )}
                </div>
                {task.status === 'PROCESSED' && (
                  <button
                    style={S.taskDownloadBtn}
                    title="Download generated files"
                    disabled={downloadingId === task.id}
                    onClick={e => { e.stopPropagation(); handleDownload(task); }}
                  >{downloadingId === task.id ? '⏳' : '⬇ Download'}</button>
                )}
                {task.status === 'QUEUED' && task.createdBy === user?.id && (
                  <button
                    style={S.taskDeleteBtn}
                    title="Delete queued task"
                    onClick={e => { e.stopPropagation(); onRequestDelete?.(task); }}
                  >🗑</button>
                )}
                <UserTag name={task.queuedBy} at={task.createdAt} />
                <span style={S.chevron}>{expanded ? '▾' : '▸'}</span>
              </div>

              {expanded && (
                <div style={S.taskBody}>
                  <div style={S.taskDetailItem}>
                    <span style={S.taskDetailLabel}>Command</span>
                    <span style={S.taskDetailVal}>{task.commandName ?? '—'}</span>
                  </div>
                  <div style={S.taskDetailItem}>
                    <span style={S.taskDetailLabel}>Queued by</span>
                    <span style={S.taskDetailVal}>{task.queuedBy}</span>
                  </div>
                  <div style={S.taskDetailItem}>
                    <span style={S.taskDetailLabel}>Files</span>
                    {task.files?.length ? (
                      <div style={S.taskFileTreeBox}>
                        <FileTreeView node={buildFileTreeFromList(task.files)} query="" />
                      </div>
                    ) : (
                      <span style={S.taskDetailVal}>
                        {task.fileCount ? `${task.fileCount} file${task.fileCount !== 1 ? 's' : ''}` : 'Full project'}
                      </span>
                    )}
                  </div>
                  <ResultContentsItem task={task} />
                  <div style={S.taskDatesRight}>
                    <span style={S.taskDate}>Created {fmtDate(task.createdAt)}</span>
                    <span style={S.taskDate}>Updated {fmtDate(task.updatedAt)}</span>
                  </div>
                  {task.output && (
                    <pre style={S.taskOutput}>{task.output}</pre>
                  )}
                  {task.status === 'FAILED' && (
                    <div style={S.taskErrorBox}>
                      <span style={S.taskErrorTitle}>❌ Error details</span>
                      <span style={S.taskErrorMsg}>
                        {taskErrorSummary(task) ?? 'The task failed, but no error details were recorded.'}
                      </span>
                      {parseProcessingNotes(task.errorDetails).length > 1 && (
                        <>
                          <span style={S.taskDetailLabel}>Processing log</span>
                          <div style={S.taskErrorLog}>
                            {parseProcessingNotes(task.errorDetails).map((e, i, all) => (
                              <div key={i} style={i === all.length - 1 ? S.taskErrorLogLast : undefined}>
                                {e.at && <span style={S.taskErrorLogTime}>{fmtDate(e.at)}</span>}
                                {e.message}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {(task.status === 'PROCESSING' || task.status === 'QUEUED') && (
                    <div style={S.taskWaitingRow}>
                      <span style={S.taskWaiting}>
                        {task.status === 'PROCESSING' && '⚙️ Processing…'}
                        {task.status === 'QUEUED' && '⏳ Waiting in queue…'}
                      </span>
                    </div>
                  )}
                  {task.outputId != null && (
                    <button style={S.linkBtn} onClick={() => onViewResult(task.outputId)}>
                      → View Result
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {visibleTasks.length > 0 && (
        <div style={S.pagination}>
          <div style={S.paginationBtns}>
            <button
              style={{ ...S.pageBtn, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              disabled={currentPage === 1}
              onClick={() => setPage(p => p - 1)}
            >
              ← Prev
            </button>
            <span style={S.pageIndicator}>Page {currentPage} of {totalPages}</span>
            <button
              style={{ ...S.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              disabled={currentPage === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Results panel ─────────────────────────────────────────────────────────────

const TYPE_CFG = {
  md:   { icon: '📝', label: 'Markdown', viewable: true  },
  json: { icon: '📋', label: 'JSON',     viewable: true  },
  text: { icon: '📃', label: 'Text',     viewable: true  },
  xml:  { icon: '📄', label: 'XML',      viewable: true  },
  pdf:  { icon: '📕', label: 'PDF',      viewable: false },
  zip:  { icon: '🗜️',  label: 'ZIP',      viewable: false },
};

const RESULTS_PAGE_SIZE = 10;

function ResultsPanel({ results, tasks, jumpToId, onJumpClear, loading, userFilter, onUserFilterChange, userOptions }) {
  const [selectedId, setSelectedId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const selectedTask = tasks.find(t => t.id === selectedId) ?? null;

  const q = search.trim().toLowerCase();
  const visibleResults = results
    .filter(r => matchesUser(r, userFilter))
    .filter(r => !q || r.title.toLowerCase().includes(q))
    .slice()
    .sort((a, b) => {
      const av = sortKey === 'id' ? a.id : apiTimestampToDate(a[sortKey] ?? a.createdAt)?.getTime() ?? 0;
      const bv = sortKey === 'id' ? b.id : apiTimestampToDate(b[sortKey] ?? b.createdAt)?.getTime() ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, userFilter]);

  const totalPages = Math.max(1, Math.ceil(visibleResults.length / RESULTS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedResults = visibleResults.slice(
    (currentPage - 1) * RESULTS_PAGE_SIZE,
    currentPage * RESULTS_PAGE_SIZE
  );

  useEffect(() => {
    if (jumpToId != null) {
      const r = results.find(x => x.id === jumpToId);
      if (r) setSelectedId(r.id);
      onJumpClear();
    }
  }, [jumpToId, onJumpClear, results]);

  async function handleDownload(result) {
    if (downloadingId || result.taskId == null) return;
    setDownloadingId(result.id);
    try {
      const { blob, filename } = await apiDownloadGeneratedCode(result.taskId);
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div style={S.panelWrap}>
      <div style={S.panelHeader}>
        <span style={S.panelTitle}>📊 Results</span>
        <span style={S.panelSub}>{visibleResults.length} file{visibleResults.length !== 1 ? 's' : ''}</span>
      </div>
      <div style={S.taskToolbar}>
        <input
          type="text"
          autoComplete="off"
          placeholder="Search by command…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={S.taskSearchInput}
        />
        <div style={S.taskFilterGroup}>
          <UserFilterSelect value={userFilter} onChange={onUserFilterChange} options={userOptions} />
          <span style={S.taskFilterLabel}>Sort by</span>
          <select style={S.taskSelect} value={sortKey} onChange={e => setSortKey(e.target.value)}>
            <option value="id">Task ID</option>
            <option value="createdAt">Created date</option>
            <option value="updatedAt">Updated date</option>
          </select>
          <button
            style={S.sortDirBtn}
            title="Toggle sort direction"
            onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
          >
            {sortDir === 'desc' ? '↓ Newest first' : '↑ Oldest first'}
          </button>
        </div>
      </div>
      <div style={S.resultsSplit}>
        <div style={S.resultsList}>
        <div style={S.resultsListScroll}>
          {loading && <PageLoader label="Loading results…" minHeight={200} />}
          {!loading && visibleResults.length === 0 && (
            <div style={S.placeholder}>{results.length === 0 ? 'No results yet.' : 'No results match the current search.'}</div>
          )}
          {!loading && pagedResults.map(result => {
            const cfg = TYPE_CFG[result.type] ?? { icon: '📄', label: result.type, viewable: false };
            const isActive = selectedId === result.id;
            return (
              <div
                key={result.id}
                style={{ ...S.resultRow, ...(isActive ? S.resultRowActive : {}), cursor: 'pointer' }}
                onClick={() => setSelectedId(result.id)}
              >
                <span style={S.resultTypeIcon}>{cfg.icon}</span>
                <span style={S.taskId}>#{result.id}</span>
                <div style={S.resultInfo}>
                  <span style={S.resultTitle}>{result.title}</span>
                  <span style={S.resultMeta}>
                    {cfg.label}{result.size ? ` · ${fmtSize(result.size)}` : ''}
                  </span>
                  <span style={S.userTagRow}><UserTag name={result.queuedBy} at={result.createdAt} /></span>
                </div>
                <div style={S.resultActions}>
                  <button
                    style={S.taskDownloadBtn}
                    onClick={e => { e.stopPropagation(); handleDownload(result); }}
                    disabled={result.taskId == null || downloadingId === result.id}
                    title="Download generated files"
                  >
                    {downloadingId === result.id ? '⏳' : '⬇ Download'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {visibleResults.length > 0 && (
          <div style={S.pagination}>
            <div style={S.paginationBtns}>
              <button
                style={{ ...S.pageBtn, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                disabled={currentPage === 1}
                onClick={() => setPage(p => p - 1)}
              >
                ← Prev
              </button>
              <span style={S.pageIndicator}>Page {currentPage} of {totalPages}</span>
              <button
                style={{ ...S.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                disabled={currentPage === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        )}
        </div>

        {selectedTask ? (
          <div style={S.previewPane}>
            <div style={S.previewBar}>
              <span>🗜️ {selectedTask.commandName ?? 'Result'} (#{selectedTask.id})</span>
              <button style={S.iconBtn} onClick={() => setSelectedId(null)}>✕</button>
            </div>
            <div style={S.taskBody}>
              <div style={S.taskDetailItem}>
                <span style={S.taskDetailLabel}>Command</span>
                <span style={S.taskDetailVal}>{selectedTask.commandName ?? '—'}</span>
              </div>
              <div style={S.taskDetailItem}>
                <span style={S.taskDetailLabel}>Queued by</span>
                <span style={S.taskDetailVal}>{selectedTask.queuedBy}</span>
              </div>
              <div style={S.taskDetailItem}>
                <span style={S.taskDetailLabel}>Files</span>
                {selectedTask.files?.length ? (
                  <div style={S.taskFileTreeBox}>
                    <FileTreeView node={buildFileTreeFromList(selectedTask.files)} query="" />
                  </div>
                ) : (
                  <span style={S.taskDetailVal}>
                    {selectedTask.fileCount ? `${selectedTask.fileCount} file${selectedTask.fileCount !== 1 ? 's' : ''}` : 'Full project'}
                  </span>
                )}
              </div>
              <ResultContentsItem task={selectedTask} />
              <div style={S.taskDatesRight}>
                <span style={S.taskDate}>Created {fmtDate(selectedTask.createdAt)}</span>
                <span style={S.taskDate}>Updated {fmtDate(selectedTask.updatedAt)}</span>
              </div>
              {selectedTask.output && (
                <pre style={S.taskOutput}>{selectedTask.output}</pre>
              )}
            </div>
          </div>
        ) : (
          <div style={S.previewEmpty}>
            <span>Select a result to view details</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Commands panel — 3-step wizard ───────────────────────────────────────────

const WIZARD_STEPS = [
  { id: 1, label: 'Select Source' },
  { id: 2, label: 'Select Command' },
  { id: 3, label: 'Task List' },
];

function CommandsPanel({ project, commands, tasks, onTaskCreated }) {
  const { user }                        = useAuth();
  const [step, setStep]                 = useState(1);
  const [wizFiles, setWizFiles]         = useState([]);
  const [tree, setTree]                 = useState(null);
  const [resourcePath, setResourcePath] = useState('');
  const [treeLoading, setTreeLoading]   = useState(true);
  const [selectedCmd, setSelectedCmd]   = useState(null);
  const [submitting, setSubmitting]     = useState(false);
  const [hoveredCmd, setHoveredCmd]     = useState(null);
  const [lastQueued, setLastQueued]     = useState(null);
  const [cmdSearch, setCmdSearch]       = useState('');
  // Collapsed by default — see the note on the other treeExpanded state above.
  const [treeExpanded, setTreeExpanded] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(() => new Set());
  // Folders left collapsed by the last bulk expand for being too large —
  // see the note on the other skippedFolders above.
  const [skippedFolders, setSkippedFolders] = useState([]);

  // Memoized — see the note on the other topNodes above.
  const topNodes = useMemo(() => topLevelNodesFromTree(tree), [tree]);

  function toggleExpandAll() {
    setTreeExpanded(v => {
      const next = !v;
      if (next) {
        const all = [];
        const skipped = [];
        collectFolderPaths(topNodes, '', all, skipped);
        setExpandedPaths(new Set(all));
        setSkippedFolders(skipped);
      } else {
        setExpandedPaths(new Set());
        setSkippedFolders([]);
      }
      return next;
    });
  }

  function toggleFolder(fullPath) {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(fullPath)) next.delete(fullPath); else next.add(fullPath);
      return next;
    });
  }

  useEffect(() => {
    setTreeLoading(true);
    apiGetProjectSourceTree(project.id)
      .then(data => {
        setResourcePath(data?.resourcePath ?? '');
        setTree(data?.tree ?? null);
        // Show fully expanded once loaded — see the note on loadTree above.
        const all = [];
        const skipped = [];
        collectFolderPaths(topLevelNodesFromTree(data?.tree), '', all, skipped);
        setExpandedPaths(new Set(all));
        setSkippedFolders(skipped);
        setTreeExpanded(true);
      })
      .catch(() => setTree(null))
      .finally(() => setTreeLoading(false));
  }, [project.id]);

  function togglePaths(paths) {
    setWizFiles(prev => {
      const allSel = paths.every(p => prev.includes(p));
      if (allSel) return prev.filter(p => !paths.includes(p));
      return [...new Set([...prev, ...paths])];
    });
  }

  // Same as the other panel's selectAllVisible/deselectAll — this panel has
  // no search filter, so "visible" here just means the whole tree (topNodes).
  function selectAllVisible() {
    const all = [];
    topNodes.forEach(n => collectPaths(n, '', all));
    setWizFiles(prev => [...new Set([...prev, ...all])]);
  }

  function deselectAll() {
    setWizFiles([]);
  }

  async function handleAddToQueue() {
    if (!selectedCmd || submitting) return;
    setSubmitting(true);
    try {
      const files = buildSourceFileList(resourcePath, wizFiles);
      const sourceFileInfo = files.length ? JSON.stringify(files) : null;
      const queued = await apiAddToCommandQueue({
        fkCommandId:    selectedCmd.id,
        fkProjectId:    project.id,
        sourceFileInfo,
        userId:         user?.id ?? 0,
      });
      const task = {
        id:          queued.commandQueueId ?? Date.now(),
        commandId:   selectedCmd.id,
        commandName: selectedCmd.name,
        status:      queued.queueStatus ?? 'QUEUED',
        files:       files.length ? files : null,
        fileCount:   wizFiles.length || null,
        queuedBy:    user?.name ?? '—',
        createdBy:   queued.createdBy ?? user?.id ?? 0,
        createdAt:   queued.createdAt ?? new Date().toISOString(),
        completedAt: queued.completedAt ?? null,
        errorDetails: null, output: null, outputId: null,
      };
      onTaskCreated(task);
      setLastQueued(task.id);
    } catch (err) {
      console.error('Add to queue failed:', err);
    } finally {
      setSubmitting(false);
    }
  }

  const queueHeaders = ['#', 'COMMAND', 'STATUS', 'FILES', 'QUEUED AT'];
  const activeTasks = tasks.filter(t => t.status === 'QUEUED' || t.status === 'PROCESSING');

  return (
    <div style={SW.wizard}>

      {/* ── Step progress bar ── */}
      <div style={SW.stepBar}>
        <div style={SW.stepTrack}>
          {WIZARD_STEPS.map((s, i) => {
            const done   = step > s.id;
            const active = step === s.id;
            return [
              <div key={`step-${s.id}`} style={SW.stepItem}>
                <div style={{
                  ...SW.stepCircle,
                  ...(active ? SW.stepCircleActive : done ? SW.stepCircleDone : {}),
                }}>
                  {done ? '✓' : s.id}
                </div>
                <span style={{
                  ...SW.stepLabel,
                  ...(active ? SW.stepLabelActive : done ? SW.stepLabelDone : {}),
                }}>
                  {s.label}
                </span>
              </div>,
              i < WIZARD_STEPS.length - 1 ? (
                <div key={`line-${s.id}`} style={{
                  ...SW.stepConnector,
                  ...(done ? SW.stepConnectorDone : {}),
                }} />
              ) : null,
            ];
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          STEP 1 — Select Source Files
          ══════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div style={SW.stepBody}>
          <div style={SW.stepBanner}>
            <div style={SW.stepBannerNum}>1</div>
            <div style={SW.stepBannerText}>
              <span style={SW.stepBannerTitle}>Select Source Files</span>
              <span style={SW.stepBannerSub}>Select files to continue.</span>
            </div>
            {wizFiles.length > 0 && (
              <span style={SW.stepBannerBadge}>
                {wizFiles.length} file{wizFiles.length !== 1 ? 's' : ''} selected
              </span>
            )}
            <button style={SW.expandAllBtn} onClick={selectAllVisible} title="Select every file currently shown">
              ☑ Select All
            </button>
            <button style={SW.expandAllBtn} onClick={deselectAll} title="Clear all selected files">
              ☐ Deselect All
            </button>
            <button
              style={SW.expandAllBtn}
              onClick={toggleExpandAll}
              title={treeExpanded ? 'Collapse all folders' : 'Expand all folders'}
            >
              {treeExpanded ? '⊟ Collapse All' : '⊞ Expand All'}
            </button>
            {treeExpanded && skippedFolders.length > 0 && (
              <span
                style={SW.expandAllWarning}
                title={`Too large to auto-expand — open individually:\n${skippedFolders.join('\n')}`}
              >
                ⚠ {skippedFolders.length} large folder{skippedFolders.length !== 1 ? 's' : ''} left collapsed
              </span>
            )}
          </div>

          <div style={{ ...SW.stepScroll, overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {treeLoading && <PageLoader label="Loading source tree…" minHeight={200} />}
            {!treeLoading && !tree && <div style={S.placeholder}>No source tree available.</div>}
            {!treeLoading && tree && topNodes.length > 0 && (
              <VirtualSourceTree
                nodes={topNodes}
                selectedFiles={wizFiles}
                onTogglePaths={togglePaths}
                expandedPaths={expandedPaths}
                onToggleFolder={toggleFolder}
                forceExpand={false}
              />
            )}
          </div>

          <div style={SW.footer}>
            <span style={SW.footerHint}>
              {wizFiles.length > 0
                ? `${wizFiles.length} file${wizFiles.length !== 1 ? 's' : ''} selected.`
                : 'Select at least one file to continue.'}
            </span>
            <button
              style={{ ...SW.nextBtn, opacity: wizFiles.length > 0 ? 1 : 0.4, cursor: wizFiles.length > 0 ? 'pointer' : 'not-allowed' }}
              disabled={wizFiles.length === 0}
              onClick={() => setStep(2)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          STEP 2 — Select Command
          ══════════════════════════════════════════════════════ */}
      {step === 2 && (
        <div style={SW.stepBody}>
          <div style={SW.stepBanner}>
            <div style={SW.stepBannerNum}>2</div>
            <div style={SW.stepBannerText}>
              <span style={SW.stepBannerTitle}>Select a Command</span>
              <span style={SW.stepBannerSub}>Choose one command to run on your selected source.</span>
            </div>
            {selectedCmd && (
              <span style={SW.stepBannerBadge}>{selectedCmd.name}</span>
            )}
          </div>

          <div style={SW.searchBar}>
            <span style={SW.searchIcon}>&#128269;</span>
            <input
              style={SW.searchInput}
              type="text"
              autoComplete="off"
              placeholder="Search commands..."
              value={cmdSearch}
              onChange={e => setCmdSearch(e.target.value)}
            />
            {cmdSearch && (
              <button style={SW.searchClear} onClick={() => setCmdSearch('')}>✕</button>
            )}
          </div>

          <div style={SW.stepScroll}>
            <div style={SW.cmdGrid}>
              {(() => {
                const q = cmdSearch.trim().toLowerCase();
                const filtered = q
                  ? commands.filter(cmd =>
                      cmd.name.toLowerCase().includes(q) ||
                      cmd.description.toLowerCase().includes(q))
                  : commands;
                if (commands.length === 0) return (
                  <div style={S.placeholder}>No commands assigned to this project.</div>
                );
                if (filtered.length === 0) return (
                  <div style={S.placeholder}>No commands match "{cmdSearch}".</div>
                );
                return filtered.map(cmd => {
                  const isSelected = selectedCmd?.id === cmd.id;
                  const isHovered  = hoveredCmd === cmd.id;
                  return (
                    <button
                      key={cmd.id}
                      style={{
                        ...SW.cmdCard,
                        ...(isSelected ? SW.cmdCardSelected : isHovered ? SW.cmdCardHover : {}),
                      }}
                      onMouseEnter={() => setHoveredCmd(cmd.id)}
                      onMouseLeave={() => setHoveredCmd(null)}
                      onClick={() => setSelectedCmd(cmd)}
                    >
                      <div style={{
                        ...SW.cmdNum,
                        ...(isSelected ? SW.cmdNumSelected : isHovered ? SW.cmdNumHover : {}),
                      }}>
                        {isSelected ? '✓' : cmd.order}
                      </div>
                      <div style={SW.cmdInfo}>
                        <span style={SW.cmdName}>{cmd.name}</span>
                        <span style={SW.cmdDesc}>{cmd.description}</span>
                      </div>
                      {isSelected
                        ? <span style={SW.cmdCheck}>✓ Selected</span>
                        : <span style={{ ...SW.cmdArrow, ...(isHovered ? SW.cmdArrowHover : {}) }}>→</span>
                      }
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          <div style={SW.footer}>
            <button style={SW.backBtn} onClick={() => setStep(1)}>← Back</button>
            <span style={SW.footerHint}>
              {selectedCmd ? `"${selectedCmd.name}" selected.` : 'Select a command to continue.'}
            </span>
            <button
              style={{ ...SW.nextBtn, opacity: selectedCmd ? 1 : 0.4, cursor: selectedCmd ? 'pointer' : 'not-allowed' }}
              disabled={!selectedCmd}
              onClick={() => setStep(3)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          STEP 3 — Command Queue
          ══════════════════════════════════════════════════════ */}
      {step === 3 && (
        <div style={SW.stepBody}>
          <div style={SW.stepBanner}>
            <div style={SW.stepBannerNum}>3</div>
            <div style={SW.stepBannerText}>
              <span style={SW.stepBannerTitle}>Task Queue</span>
              <span style={SW.stepBannerSub}>Review your selection and add to the execution queue.</span>
            </div>
          </div>

          <div style={SW.stepScroll}>
            {/* ── Selection review card ── */}
            <div style={SW.reviewCard}>
              <div style={SW.reviewItem}>
                <span style={SW.reviewIcon}>📂</span>
                <div style={SW.reviewText}>
                  <span style={SW.reviewLabel}>Source</span>
                  <span style={SW.reviewVal}>
                    {wizFiles.length > 0
                      ? `${wizFiles.length} file${wizFiles.length !== 1 ? 's' : ''} selected`
                      : 'Full project'}
                  </span>
                </div>
                <button style={SW.reviewEdit} onClick={() => setStep(1)}>Change</button>
              </div>
              <div style={SW.reviewDivider} />
              <div style={SW.reviewItem}>
                <span style={SW.reviewIcon}>⚙️</span>
                <div style={SW.reviewText}>
                  <span style={SW.reviewLabel}>Command</span>
                  <span style={SW.reviewVal}>{selectedCmd?.name ?? '—'}</span>
                </div>
                <button style={SW.reviewEdit} onClick={() => setStep(2)}>Change</button>
              </div>
            </div>

            {/* ── Queue table section ── */}
            <div style={SW.queueSection}>
              <div style={SW.queueSectionBar}>
                <span style={SW.queueSectionLabel}>Task Queue</span>
                <span style={SW.queueCount}>{activeTasks.length} task{activeTasks.length !== 1 ? 's' : ''}</span>
              </div>
              {lastQueued && (
                <div style={SW.successBanner}>
                  ✓ Command added to queue successfully.
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={SW.queueTable}>
                  <thead>
                    <tr>
                      {queueHeaders.map(h => (
                        <th key={h} style={SW.queueTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeTasks.length === 0 ? (
                      <tr>
                        <td colSpan={queueHeaders.length} style={SW.queueEmpty}>
                          No tasks queued yet. Add one below.
                        </td>
                      </tr>
                    ) : (
                      activeTasks.map((task, idx) => {
                        const cfg = STATUS_CFG_SRC[task.status] ?? STATUS_CFG_SRC.QUEUED;
                        const isNew = task.id === lastQueued;
                        return (
                          <tr key={task.id} style={isNew ? SW.queueRowNew : {}}>
                            <td style={SW.queueTd}>{idx + 1}</td>
                            <td style={{ ...SW.queueTd, fontWeight: 600 }}>{task.commandName}</td>
                            <td style={SW.queueTd}>
                              <span style={{ ...SW.statusPill, color: cfg.color, background: cfg.bg }}>
                                {cfg.label}
                              </span>
                            </td>
                            <td style={SW.queueTd}>
                              {task.fileCount != null ? `${task.fileCount} file${task.fileCount !== 1 ? 's' : ''}` : 'Full project'}
                            </td>
                            <td style={SW.queueTd}>{fmtDate(task.createdAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={SW.footer}>
            <button style={SW.backBtn} onClick={() => setStep(2)}>← Back</button>
            <span style={SW.footerHint} />
            <button
              style={{ ...SW.submitBtn, opacity: (submitting || !selectedCmd) ? 0.5 : 1 }}
              disabled={submitting || !selectedCmd}
              onClick={handleAddToQueue}
            >
              {submitting ? '⏳ Adding…' : '▶ Add to Queue'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Source panel styles ───────────────────────────────────────────────────────

const SX = {
  page: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg-page)' },

  pageHeader: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '14px 24px', borderBottom: '1px solid var(--border)',
    background: '#fff', flexShrink: 0,
  },
  pageTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' },
  pageSub:   { fontSize: 12, color: 'var(--text-muted)', marginTop: 2 },
  projectPill: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'var(--bg-page)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '6px 14px', marginLeft: 'auto',
  },
  projectPillLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' },
  projectPillName:  { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },

  topSplit: { display: 'flex', flex: '0 0 55%', minHeight: 0, overflow: 'hidden', borderBottom: '1px solid var(--border)' },

  sectionBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 16px', borderBottom: '1px solid var(--border)',
    background: 'var(--bg-card)', flexShrink: 0,
  },
  sectionLabel: { fontSize: 10, fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-muted)' },
  selectedBadge: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 700,
    background: 'var(--primary)', color: '#fff',
    borderRadius: 12, padding: '2px 10px',
  },

  treePane:  { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid var(--border)' },
  treeScroll:{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' },

  treeRow: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '3px 8px', cursor: 'pointer', userSelect: 'none',
    borderRadius: 4, margin: '1px 4px',
    transition: 'background 0.1s',
  },
  treeRowChecked: { background: 'rgba(21,101,192,0.07)' },
  checkbox:   { flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary)', width: 14, height: 14 },
  chevron:    { fontSize: 10, color: 'var(--text-muted)', width: 12, flexShrink: 0, cursor: 'pointer' },
  folderName: { fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  folderCount:{ fontSize: 10, color: 'var(--text-muted)', background: 'rgba(0,0,0,0.06)', borderRadius: 8, padding: '1px 6px', flexShrink: 0 },
  fileName:   { fontSize: 12, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // Right-click menu on a source-tree row (FPB-70) — deletes just that one
  // file, or a folder and everything under it.
  contextMenu: {
    position: 'fixed', zIndex: 1000, minWidth: 160, padding: 4,
    background: '#fff', border: '1px solid var(--border)', borderRadius: 6,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
  contextMenuItem: {
    display: 'block', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', borderRadius: 4,
    padding: '6px 10px', fontSize: 12, color: '#f85149', cursor: 'pointer',
  },

  cmdPane: { width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' },
  cmdPaneBody: { flex: 1, display: 'flex', flexDirection: 'column', padding: 16, gap: 12, overflow: 'hidden' },
  cmdSelect: {
    width: '100%', padding: '9px 10px', fontSize: 13, borderRadius: 7,
    border: '2px solid var(--primary)', background: 'var(--bg-page)',
    color: 'var(--text-primary)', outline: 'none', cursor: 'pointer',
  },
  cmdFileList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 },
  cmdFileItem: { fontSize: 11, color: 'var(--text-muted)', padding: '2px 6px', background: 'rgba(0,0,0,0.03)', borderRadius: 4 },
  execBtn: {
    width: '100%', padding: '10px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8,
    transition: 'opacity 0.15s',
  },
  execHint: { fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' },

  queueSection: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  queueScroll:  { flex: 1, overflowY: 'auto' },
  queueTable:   { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  queueTh: {
    padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 800,
    letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--text-muted)',
    borderBottom: '2px solid var(--border)', background: 'var(--bg-card)',
    whiteSpace: 'nowrap',
  },
  queueTd: { padding: '10px 14px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', whiteSpace: 'nowrap' },
  statusPill: { fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 12 },
  filesLink:  { fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer' },
  deleteBtn:  { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#f85149', padding: '2px 6px' },
};

// ── Wizard styles ─────────────────────────────────────────────────────────────

const SW = {
  // ── Outer wrapper ─────────────────────────────────────────────────
  wizard: {
    display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
    background: 'var(--bg-page)',
  },

  // ── Step progress header ──────────────────────────────────────────
  stepBar: {
    flexShrink: 0, background: '#fff',
    borderBottom: '1px solid var(--border)',
    padding: '0 32px',
  },
  stepTrack: {
    display: 'flex', alignItems: 'center',
    maxWidth: 640, margin: '0 auto', height: 72,
  },
  stepItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 6, flexShrink: 0, position: 'relative',
  },
  stepCircle: {
    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 800,
    background: '#fff', border: '2px solid var(--border)', color: 'var(--text-muted)',
    boxSizing: 'border-box',
  },
  stepCircleActive: {
    background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff',
    boxShadow: '0 0 0 4px rgba(230,81,0,0.12)',
  },
  stepCircleDone: {
    background: '#1B7F3B', borderColor: '#1B7F3B', color: '#fff',
  },
  stepLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: 'var(--text-muted)',
    textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 1,
  },
  stepLabelActive: { color: 'var(--primary)' },
  stepLabelDone:   { color: '#1B7F3B' },
  stepConnector: {
    flex: 1, height: 2, background: 'var(--border)', margin: '0 12px',
    marginBottom: 22, /* push up to align with circle center */
  },
  stepConnectorDone: { background: '#1B7F3B' },

  // ── Step title banner (shown below the progress bar) ──────────────
  stepBanner: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '16px 28px', flexShrink: 0,
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-page)',
  },
  stepBannerNum: {
    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
    background: 'var(--primary)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 800,
  },
  stepBannerNumDone: { background: '#1B7F3B' },
  stepBannerText: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  stepBannerTitle: { fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 },
  stepBannerSub:   { fontSize: 12, color: 'var(--text-muted)' },
  stepBannerBadge: {
    background: 'rgba(230,81,0,0.1)', color: 'var(--primary)',
    border: '1px solid rgba(230,81,0,0.25)',
    fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 99,
    flexShrink: 0,
  },

  // ── Shared step body ──────────────────────────────────────────────
  stepBody: {
    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  stepScroll: { flex: 1, overflowY: 'auto' },

  // ── Command search bar ────────────────────────────────────────────
  searchBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 20px',
    borderBottom: '1px solid var(--border)',
    background: '#fff', flexShrink: 0,
  },
  searchIcon: { fontSize: 16, color: 'var(--text-muted)', flexShrink: 0 },
  searchInput: {
    flex: 1, border: '1.5px solid var(--border)', borderRadius: 8,
    padding: '8px 12px', fontSize: 13, outline: 'none',
    background: 'var(--bg-page)', color: 'var(--text-primary)',
    fontFamily: 'inherit',
  },
  searchClear: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-muted)', fontSize: 13, padding: '4px 6px',
    borderRadius: 6, lineHeight: 1,
  },
  expandAllBtn: {
    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
    border: '1.5px solid var(--border)', borderRadius: 8,
    padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    background: 'var(--bg-page)', color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
  },
  // Shown next to Expand All when one or more folders were too large to
  // auto-expand (see AUTO_EXPAND_MAX_ITEMS) — hover for which ones.
  expandAllWarning: {
    fontSize: 11, fontWeight: 600, color: '#9a6700',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    flexShrink: 1, cursor: 'default',
  },
  // "Delete Selected" toolbar button (FPB-70) — red-tinted only once something
  // is actually selected, so it doesn't read as a live danger button at rest.
  deleteSelectedBtnActive: {
    borderColor: 'rgba(248,81,73,0.4)', color: '#f85149',
  },
  deleteSelectedBtnDisabled: {
    opacity: 0.4, cursor: 'not-allowed',
  },

  // ── Footer navigation ─────────────────────────────────────────────
  footer: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 28px', borderTop: '2px solid var(--border)',
    background: '#fff', flexShrink: 0,
  },
  footerHint: { fontSize: 12, color: 'var(--text-muted)', flex: 1 },
  nextBtn: {
    padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8,
    boxShadow: '0 2px 10px rgba(230,81,0,0.25)',
    letterSpacing: '0.2px',
  },
  skipBtn: {
    padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    background: 'transparent', color: 'var(--text-muted)',
    border: '1px solid var(--border)', borderRadius: 8,
  },
  backBtn: {
    padding: '9px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    background: 'transparent', color: 'var(--text-muted)',
    border: '1px solid var(--border)', borderRadius: 8,
  },
  submitBtn: {
    padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8,
    boxShadow: '0 2px 10px rgba(230,81,0,0.25)', transition: 'opacity 0.15s',
  },

  // ── Step 2: Command cards ─────────────────────────────────────────
  cmdGrid: { padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 12 },
  cmdCard: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px',
    background: '#fff', border: '2px solid var(--border)', borderRadius: 12,
    cursor: 'pointer', textAlign: 'left', width: '100%',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.12s',
  },
  cmdCardHover: {
    borderColor: 'var(--primary)', boxShadow: '0 6px 20px rgba(230,81,0,0.14)',
    transform: 'translateY(-2px)',
  },
  cmdNum: {
    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
    background: 'var(--bg-page)', border: '2px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 800, color: 'var(--text-muted)',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  },
  cmdNumHover: { background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff' },
  cmdInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  cmdName: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
  cmdDesc: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 },
  cmdArrow: {
    fontSize: 20, color: 'var(--text-muted)', flexShrink: 0,
    transition: 'color 0.15s, transform 0.15s',
  },
  cmdArrowHover: { color: 'var(--primary)', transform: 'translateX(4px)' },
  cmdCardSelected: {
    borderColor: 'var(--primary)', background: 'rgba(230,81,0,0.03)',
    boxShadow: '0 0 0 3px rgba(230,81,0,0.1)',
  },
  cmdNumSelected: { background: '#1B7F3B', borderColor: '#1B7F3B', color: '#fff' },
  cmdCheck: {
    fontSize: 11, fontWeight: 700, color: '#1B7F3B',
    background: 'rgba(27,127,59,0.1)', border: '1px solid rgba(27,127,59,0.25)',
    borderRadius: 6, padding: '4px 10px', flexShrink: 0, whiteSpace: 'nowrap',
  },

  // ── Step 3: Review card ───────────────────────────────────────────
  reviewCard: {
    margin: '20px 28px 0',
    background: '#fff', border: '1.5px solid var(--border)', borderRadius: 12,
    overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  reviewItem: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
  },
  reviewIcon:    { fontSize: 20, flexShrink: 0 },
  reviewText:    { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  reviewLabel:   { fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.7px', color: 'var(--text-muted)' },
  reviewVal:     { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  reviewEdit:    {
    fontSize: 12, fontWeight: 600, color: 'var(--primary)',
    background: 'none', border: '1px solid rgba(230,81,0,0.3)', borderRadius: 6,
    padding: '4px 12px', cursor: 'pointer', flexShrink: 0,
  },
  reviewDivider: { height: 1, background: 'var(--border)', margin: '0 20px' },

  // ── Step 3: Queue table ───────────────────────────────────────────
  queueSection: { margin: '20px 28px 28px' },
  queueSectionBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 10,
  },
  queueSectionLabel: {
    fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.8px', color: 'var(--text-muted)',
  },
  queueCount: {
    fontSize: 10, fontWeight: 700,
    background: 'var(--bg-page)', border: '1px solid var(--border)',
    color: 'var(--text-muted)', borderRadius: 99, padding: '1px 8px',
  },
  successBanner: {
    background: 'rgba(27,127,59,0.08)', border: '1px solid rgba(27,127,59,0.25)',
    borderRadius: 8, padding: '9px 16px', marginBottom: 10,
    fontSize: 13, fontWeight: 600, color: '#1B7F3B',
  },
  queueTable: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12,
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden',
  },
  queueTh: {
    padding: '9px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)',
    background: 'var(--bg-page)', borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  queueTd: {
    padding: '11px 14px', borderBottom: '1px solid var(--border)',
    color: 'var(--text-primary)', whiteSpace: 'nowrap',
  },
  queueEmpty: {
    padding: '32px 20px', textAlign: 'center',
    fontSize: 13, color: 'var(--text-muted)',
  },
  queueRowNew: { background: 'rgba(27,127,59,0.05)' },
  statusPill: {
    display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.4px',
    padding: '2px 10px', borderRadius: 99, textTransform: 'uppercase',
  },
};

// ── Command input-schema modal (radio/checkbox file picker) ──────────────────
const IM = {
  // Plain in-page card — same visual language as SW.reviewCard, no overlay/backdrop,
  // since this now renders inline as part of Step 3 instead of popping up over the page.
  card: {
    margin: '0 28px', padding: '16px 18px',
    background: '#fff', border: '1.5px solid var(--border)', borderRadius: 12,
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  title: { fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' },
  body: { display: 'flex', flexDirection: 'column', gap: 18 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' },
  fieldLabel: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  required: { color: '#f85149', fontWeight: 800 },
  fieldHint: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 },
  fileList: {
    border: '1.5px solid var(--border)', borderRadius: 8,
    maxHeight: 160, overflowY: 'auto', background: 'var(--bg-page)',
  },
  fileListEmpty: { padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
    fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer',
    borderBottom: '1px solid var(--border)',
  },
  fileRowDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  fileRowNote: { fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' },
  fileCheckbox: { flexShrink: 0, accentColor: 'var(--primary)', cursor: 'pointer' },
  fieldError: { fontSize: 12, fontWeight: 600, color: '#f85149' },
};

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  // Layout
  page:    { display: 'flex', flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', position: 'relative' },
  content: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },

  // Delete-confirmation modal
  modalOverlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modalCard: {
    width: 380, maxWidth: '90vw', background: 'var(--bg-card)', borderRadius: 10,
    border: '1px solid var(--border)', boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
    padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
  },
  modalTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' },
  // --text-muted (#B07050) reads too light against the modal's white card —
  // use --text-secondary (#6B4226) instead, still visually secondary to the
  // title but with enough contrast to read clearly.
  modalBody:  { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  modalCancelBtn: {
    padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  modalDeleteBtn: {
    padding: '7px 16px', borderRadius: 6, border: 'none',
    background: '#f85149', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  modalPrimaryBtn: {
    padding: '7px 16px', borderRadius: 6, border: 'none',
    background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },

  // Upload-files-into-folder modal
  uploadDropZone: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '20px 12px', border: '2px dashed var(--border)', borderRadius: 8,
    background: 'var(--bg-page)', color: 'var(--text-primary)', fontSize: 13,
    cursor: 'pointer', textAlign: 'center',
  },
  uploadDropZoneActive: { borderColor: 'var(--primary)', background: 'rgba(21,101,192,0.07)' },
  uploadFileList: {
    maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3,
    border: '1px solid var(--border)', borderRadius: 6, padding: 4,
  },
  uploadFileItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.03)' },
  uploadFileName: { flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  uploadFileSize: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 },
  uploadFileRemoveBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
    color: 'var(--text-muted)', padding: '2px 4px', lineHeight: 1, flexShrink: 0,
  },

  // Sidebar nav
  nav: {
    width: 176, flexShrink: 0, display: 'flex', flexDirection: 'column',
    borderRight: '1px solid var(--border)', background: 'var(--bg-card)', overflow: 'hidden',
  },
  navProjectBadge: {
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary, rgba(0,0,0,0.03))',
  },
  navProjectName: {
    display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '11px 14px', background: 'none', border: 'none',
    borderLeft: '3px solid transparent', cursor: 'pointer', textAlign: 'left',
  },
  navItemActive: {
    background: 'rgba(21,101,192,0.07)', borderLeftColor: 'var(--primary)',
  },
  navIcon:  { fontSize: 15, flexShrink: 0 },
  navLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', color: 'var(--text-muted)', flex: 1 },
  navLabelActive: { color: 'var(--primary)' },
  navBadge: {
    fontSize: 9, fontWeight: 700, background: 'rgba(0,0,0,0.07)',
    color: 'var(--text-muted)', borderRadius: 8, padding: '1px 5px',
  },
  navBadgeActive: { background: 'rgba(21,101,192,0.15)', color: 'var(--primary)' },

  // Panel common
  panelWrap:   { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  panelHeader: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 20px',
    flexShrink: 0, borderBottom: '1px solid var(--border)', background: 'var(--bg-card)',
  },
  panelTitle:   { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
  panelSub:     { fontSize: 12, color: 'var(--text-muted)' },
  pathMono:     { fontSize: 11, fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 },
  panelActions: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },

  taskToolbar: {
    display: 'flex', alignItems: 'center', gap: 18, padding: '10px 20px',
    flexShrink: 0, borderBottom: '1px solid var(--border)', background: 'var(--bg-page)', flexWrap: 'wrap',
  },
  taskSearchInput: {
    flex: '0 0 220px', padding: '7px 12px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)',
    background: 'var(--bg-page)', outline: 'none',
  },
  taskTabs: { display: 'flex', gap: 4 },
  taskTab: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)',
    fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
  },
  taskTabActive: {
    background: 'var(--info-bg)', border: '1px solid rgba(21,101,192,0.3)',
    color: 'var(--primary)', fontWeight: 700,
  },
  taskTabBadge: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 10, fontSize: 10, fontWeight: 700,
    padding: '1px 6px', color: 'var(--text-muted)',
  },
  taskTabBadgeActive: {
    background: 'rgba(21,101,192,0.15)', border: '1px solid rgba(21,101,192,0.25)',
    color: 'var(--primary)',
  },
  taskFilterGroup: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  taskFilterLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 },
  taskSelect: {
    fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer',
  },
  sortDirBtn: {
    fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600,
  },
  refreshBtn: {
    fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600,
  },

  selBadge: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 600,
    background: 'rgba(21,101,192,0.1)', border: '1px solid rgba(21,101,192,0.25)',
    color: 'var(--primary)', borderRadius: 12, padding: '2px 10px',
  },
  actionBtn: {
    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    background: 'var(--bg-page)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text-primary)',
  },
  actionBtnOn: {
    background: 'rgba(21,101,192,0.08)', border: '1px solid rgba(21,101,192,0.3)',
    color: 'var(--primary)',
  },
  iconBtn:   { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: 4 },
  placeholder: { padding: '40px 20px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' },

  // Source
  sourceSplit:    { flex: 1, display: 'flex', overflow: 'hidden' },
  sourceTree:     { width: 270, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid var(--border)', padding: '6px 0 16px' },
  fileViewer:     { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  fileViewerBar:  {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '7px 14px', flexShrink: 0, borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary, rgba(0,0,0,0.03))',
    fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono, monospace)',
  },
  fileContent: {
    flex: 1, margin: 0, overflowY: 'auto', padding: '16px',
    fontSize: 12, lineHeight: 1.75, fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-primary)', background: 'var(--bg-page)',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  fileViewerEmpty: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, color: 'var(--text-muted)', background: 'var(--bg-page)',
  },

  // Tree nodes
  treeFolder:     { display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', cursor: 'pointer', userSelect: 'none' },
  treeChevron:    { fontSize: 9, color: 'var(--text-muted)', width: 10, flexShrink: 0 },
  treeFolderName: { fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', flex: 1 },
  treeCount:      { fontSize: 9, color: 'var(--text-muted)', background: 'rgba(0,0,0,0.06)', borderRadius: 8, padding: '1px 5px' },
  treeChildren:   { paddingLeft: 16 },
  treeFile:       { display: 'flex', alignItems: 'center', gap: 6, padding: '3px 12px', cursor: 'pointer', borderRadius: 4, margin: '1px 4px' },
  treeFileActive: { background: 'rgba(21,101,192,0.1)' },
  treeFileName:   { fontSize: 12, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  treeFileSize:   { fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 },

  // Tasks
  scrollArea: { flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  taskCard:   { border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)', overflow: 'hidden', flexShrink: 0 },
  taskCardOpen: { borderColor: 'rgba(21,101,192,0.3)', boxShadow: '0 2px 8px rgba(21,101,192,0.07)' },
  taskCardHeader: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', height: 44, boxSizing: 'border-box', cursor: 'pointer' },
  taskId:     { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', width: 40, flexShrink: 0 },
  statusPill: { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, width: 96, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap', flexShrink: 0 },
  taskInfo:   { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  taskTitle:  { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  taskCmd:    { fontSize: 11, color: 'var(--text-primary)' },
  taskDate:   { fontSize: 11, color: 'var(--text-primary)', flexShrink: 0 },
  userTag:    { fontSize: 11, fontWeight: 600, color: 'var(--primary)', background: 'rgba(21,101,192,0.08)', border: '1px solid rgba(21,101,192,0.2)', borderRadius: 12, padding: '2px 8px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 230, boxSizing: 'border-box', flexShrink: 0 },
  userTagName: { maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' },
  userTagSep: { opacity: 0.5 },
  userTagRow: { display: 'flex' },
  taskDeleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#f85149', padding: '2px 6px', flexShrink: 0 },
  taskDownloadBtn: { background: 'rgba(63,185,80,0.12)', border: '1px solid rgba(63,185,80,0.35)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#2e7d32', padding: '4px 10px', flexShrink: 0 },
  chevron:    { fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 },
  pagination:     { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '12px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 },
  paginationBtns: { display: 'flex', alignItems: 'center', gap: 12 },
  pageBtn:        { background: '#fff', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', padding: '5px 12px' },
  pageIndicator:  { fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 },
  taskBody:   { padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-page)', display: 'flex', flexDirection: 'column', gap: 10 },
  taskDetailItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  taskDatesRight: { display: 'flex', gap: 16, justifyContent: 'flex-end' },
  taskDetailLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 },
  taskDetailVal: { fontSize: 12, color: 'var(--text-primary)' },
  taskFileTreeBox: {
    border: '1px solid var(--border)', borderRadius: 6, padding: '6px 4px',
    maxHeight: 220, overflowY: 'auto', background: 'var(--bg-card)',
  },
  taskOutput: {
    margin: 0, padding: '12px', borderRadius: 6, maxHeight: 280, overflowY: 'auto',
    background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)',
    fontSize: 12, lineHeight: 1.65, fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-primary)', whiteSpace: 'pre-wrap',
  },
  taskErrorInline: { fontSize: 11, color: '#d1242f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  taskErrorBox: {
    display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', borderRadius: 6,
    background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.35)',
  },
  taskErrorTitle: { fontSize: 11, fontWeight: 700, color: '#d1242f', textTransform: 'uppercase', letterSpacing: 0.3 },
  taskErrorMsg: { fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  taskErrorLog: {
    maxHeight: 220, overflowY: 'auto', padding: '8px 10px', borderRadius: 6,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    fontSize: 11, lineHeight: 1.6, fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  taskErrorLogLast: { color: '#d1242f', fontWeight: 600 },
  taskErrorLogTime: { color: 'var(--text-muted)', marginRight: 8 },
  taskWaiting: { fontSize: 12, color: 'var(--text-muted)' },
  taskWaitingRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  linkBtn: {
    alignSelf: 'flex-start', padding: '4px 12px', fontSize: 11, fontWeight: 600,
    background: 'rgba(21,101,192,0.08)', color: 'var(--primary)',
    border: '1px solid rgba(21,101,192,0.25)', borderRadius: 5, cursor: 'pointer',
  },

  // Results
  resultsSplit:  { flex: 1, display: 'flex', overflow: 'hidden' },
  resultsList:   { width: 480, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid var(--border)' },
  resultsListScroll: { flex: 1, overflowY: 'auto' },
  resultRow:     { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)' },
  resultRowActive: { background: 'rgba(21,101,192,0.06)' },
  resultTypeIcon: { fontSize: 22, flexShrink: 0 },
  resultInfo:    { flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  resultTitle:   { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  resultMeta:    { fontSize: 11, color: 'var(--text-muted)' },
  resultActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  previewPane:   { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  previewBar:    {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '7px 14px', flexShrink: 0, borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary, rgba(0,0,0,0.03))',
    fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono, monospace)',
  },
  previewContent: {
    flex: 1, margin: 0, overflowY: 'auto', padding: '16px',
    fontSize: 12, lineHeight: 1.75, fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-primary)', background: 'var(--bg-page)',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  previewEmpty: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, color: 'var(--text-muted)', background: 'var(--bg-page)',
  },

  // Commands
  commandsSplit: { flex: 1, display: 'flex', overflow: 'hidden' },
  commandsList:  { flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  cmdCard: {
    display: 'flex', gap: 12, padding: '14px 16px',
    border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)',
  },
  cmdCardActive: { borderColor: 'rgba(21,101,192,0.4)', boxShadow: '0 2px 8px rgba(21,101,192,0.08)' },
  cmdOrder: {
    flexShrink: 0, width: 26, height: 26, display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: 'rgba(21,101,192,0.1)', borderRadius: '50%',
    fontSize: 10, fontWeight: 800, color: 'var(--primary)', marginTop: 2,
  },
  cmdInfo:    { flex: 1, display: 'flex', flexDirection: 'column', gap: 5 },
  cmdName:    { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  cmdDesc:    { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 },
  cmdIO:      { display: 'flex', gap: 6, alignItems: 'baseline' },
  cmdIOLabel: { fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-muted)', flexShrink: 0, width: 22 },
  cmdIOVal:   { fontSize: 12, color: 'var(--text-primary)' },
  cmdSelNote: { fontSize: 11, fontWeight: 600, color: '#3fb950' },
  goSourceBtn: {
    display: 'inline', padding: 0, background: 'none', border: 'none',
    fontSize: 11, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline',
  },
  runBtn: {
    alignSelf: 'flex-start', flexShrink: 0, padding: '7px 16px', fontSize: 12, fontWeight: 700,
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
    marginTop: 2,
  },

  // Run pane
  runPane:      { width: 340, flexShrink: 0, borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--bg-card)' },
  runPaneBar:   {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '11px 16px', flexShrink: 0, borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary, rgba(0,0,0,0.03))',
  },
  runPaneTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  runPaneBody:  { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 },
  runDesc:      { margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 },
  runSection:   { display: 'flex', gap: 8, alignItems: 'baseline' },
  runSectionLabel: { fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-muted)', flexShrink: 0 },
  runSectionVal:   { fontSize: 12, color: 'var(--text-primary)' },
  runSourceBox: {
    padding: '8px 12px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
    background: 'rgba(63,185,80,0.07)', border: '1px solid rgba(63,185,80,0.2)',
    color: 'var(--text-primary)',
  },
  paramRow:   { display: 'flex', flexDirection: 'column', gap: 5 },
  paramLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-muted)' },
  paramInput: {
    padding: '7px 10px', fontSize: 12, borderRadius: 5,
    border: '1px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-primary)', outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  runActions:  { display: 'flex', gap: 8, marginTop: 4 },
  submitBtn:   {
    flex: 1, padding: '9px', fontSize: 13, fontWeight: 700,
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  cancelBtn:   {
    padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    background: 'transparent', color: 'var(--text-muted)',
    border: '1px solid var(--border)', borderRadius: 6,
  },
  runPaneEmpty: {
    width: 340, flexShrink: 0, borderLeft: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, color: 'var(--text-muted)', padding: 20, textAlign: 'center',
    background: 'var(--bg-page)',
  },
};
